// pages/FileDeployPage.jsx
// Halaman "File Deploy" (admin-only) — alternatif WinSCP dari browser.
// Upload file .js/.jsx → diff vs file di server → deploy (backup otomatis +
// replace) → backend: restart pm2; frontend: rebuild dengan log live.
// Tab kedua: Backup & Restore (list per file, pulihkan dari backup).
//
// Mengikuti pola TableMaintenance.jsx: inline-style + primitive lokal,
// axios instance dari AuthContext (token otomatis via interceptor), useLang.

import { Fragment, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import useIsMobile from '../hooks/useIsMobile';
// Terminal sungguhan untuk desktop. Dipakai HANYA di OS Console; halaman lain
// tidak terpengaruh. CSS-nya wajib ikut diimpor — tanpa itu kursor dan
// pengukuran selnya salah, dan `fit()` menghitung kolom dari lebar sel.
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { tabsFor } from '../searchRegistry';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── useApi (sama pola dgn page lain) ────────────────────────────────────────────
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = method === 'get' || method === 'delete'
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// ── Primitive UI (selaras TableMaintenance) ─────────────────────────────────────
function Alert({ msg, type = 'info', onClose }) {
  if (!msg) return null;
  const c = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    error:   { bg: '#fee2e2', color: '#7f1d1d', border: '#fca5a5' },
    info:    { bg: 'rgb(var(--brand-100))', color: '#1e3a5f', border: '#93c5fd' },
    warning: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  }[type] || {};
  return (
    <div style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{msg}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, fontSize: 18, lineHeight: 1, marginLeft: 8 }}>×</button>}
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
function Input(props) { return <input {...props} style={{ ...inputStyle, ...(props.style || {}) }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ ...inputStyle, background: '#fff', ...(props.style || {}) }}>{children}</select>; }

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// Merender string terjemahan yang memuat placeholder {code} dan/atau {b}:
// bagian itu dibungkus <code>/<b>, sisanya teks biasa.
//
// Kenapa perlu: kalau kalimat dipotong oleh tag di JSX, tiap potongan jadi key
// terpisah ("di /awsbasis/dashboard/backend — bukan sekadar", "yang dipilih?"). Potongan
// begitu tidak bisa disusun ulang, padahal urutan kata Arab & Inggris berbeda
// dari Indonesia. Dengan helper ini kalimatnya tetap utuh sebagai satu key.
function Tx({ text, code, b, codeStyle }) {
  const cs = codeStyle || { background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 };
  const out = [];
  String(text).split(/(\{code\}|\{b\})/).forEach((part, i) => {
    if (part === '{code}') out.push(<code key={i} style={cs}>{code}</code>);
    else if (part === '{b}') out.push(<b key={i}>{b}</b>);
    else if (part) out.push(<Fragment key={i}>{part}</Fragment>);
  });
  return <>{out}</>;
}

function Btn({ children, onClick, variant = 'default', size = 'md', disabled = false }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: size === 'sm' ? 12 : 13, padding: size === 'sm' ? '5px 10px' : '8px 14px', opacity: disabled ? 0.55 : 1, transition: 'all 0.15s', fontWeight: 500 };
  const variants = {
    default: { background: '#fff',    borderColor: '#e2e8f0', color: '#475569' },
    primary: { background: 'rgb(var(--brand-600))', borderColor: 'rgb(var(--brand-600))', color: '#fff' },
    danger:  { background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' },
    warning: { background: '#fffbeb', borderColor: '#fde68a', color: '#d97706' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

function Modal({ title, children, onClose, maxWidth = 460 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#0f172a' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Empty({ icon, text }) {
  return (
    <div style={{ padding: '2.5rem', textAlign: 'center', color: '#cbd5e1' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{text}</div>
    </div>
  );
}

// Berkas ini punya primitif lokalnya sendiri (lihat catatan di kepala berkas)
// dan TIDAK meng-import dari tmShared.jsx. Badge ditambahkan di sini saat tab
// Zip Deploy dibuat; menyalinnya adalah keputusan sadar, bukan kelalaian —
// meng-import satu komponen dari halaman lain akan mengikat dua halaman yang
// selama ini sengaja berdiri sendiri.
function Badge({ text, color = '#64748b' }) {
  return (
    <span style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

// ── Util ─────────────────────────────────────────────────────────────────────
function fmtBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1048576).toFixed(2) + ' MB';
}
// Format timestamp backup "20260623-141530" → "2026-06-23 14:15:30".
// Gaya panel auto-locate. File ini memakai inline style, jadi dikumpulkan di
// satu tempat agar konsisten dan mudah diubah.
const S = {
  locateBox: (bg, border) => ({
    background: bg, border: `1px solid ${border}`, borderRadius: 10,
    padding: '10px 12px', fontSize: 13, color: '#334155', marginBottom: 12,
  }),
  locateCode: {
    background: '#fff', border: '1px solid #d1fae5', borderRadius: 4,
    padding: '1px 6px', fontSize: 12,
  },
  locateOption: (picked) => ({
    display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left',
    gap: 4, padding: '8px 10px', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 13, borderRadius: 8,
    background: picked ? '#ecfdf5' : '#fff',
    border: `1px solid ${picked ? '#6ee7b7' : '#e2e8f0'}`,
  }),
};

function fmtTs(ts) {
  const m = /^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(ts || '');
  return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}` : (ts || '—');
}

// Timestamp header diff berupa ISO UTC ("2026-07-28T07:45:44.316Z").
// Ditampilkan eksplisit di Asia/Jakarta — BUKAN zona perangkat — supaya jam
// yang terbaca sama dengan jam di log server & jadwal cron, dari laptop mana
// pun. Penambahan 'Z' tidak perlu di sini karena string-nya sudah ISO+Z
// (berbeda dengan timestamp dari SQLite, lihat catatan timezone di handoff).
function fmtIsoWib(iso, withSeconds = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const s = d.toLocaleString('id-ID', {
    timeZone: 'Asia/Jakarta', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}),
  });
  return `${s.replace(/\./g, ':')} WIB`;
}

// Diff baris sederhana (LCS). Kembalikan [{type:'eq'|'add'|'del', text}].
// Dijaga: lewati bila file sangat besar agar UI tetap responsif.
function diffLines(aText, bText) {
  const a = (aText || '').split('\n');
  const b = (bText || '').split('\n');
  if (a.length * b.length > 4000000) return null; // terlalu besar → skip detail
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: 'eq', text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: 'del', text: a[i] }); i++; }
    else { out.push({ type: 'add', text: b[j] }); j++; }
  }
  while (i < n) { out.push({ type: 'del', text: a[i++] }); }
  while (j < m) { out.push({ type: 'add', text: b[j++] }); }
  return out;
}

// Konstanta level modul tidak bisa memanggil t() (di luar komponen), jadi yang
// disimpan adalah KEY-nya; penerjemahan dilakukan di titik pakai.
const TYPE_HINT = {
  frontend:     'fdeploy.hintFrontend',
  backend:      'fdeploy.hintBackend',
  'ai-service': 'fdeploy.hintAiService',
};

// Sama persis dgn ROOTS di backend fileDeployRoutes.js. Dipakai File Explorer
// (yang bekerja dgn path absolut) untuk tahu apakah suatu file berada di bawah
// salah satu root deploy — kalau ya, riwayat diff-nya bisa ditelusuri via
// /api/file-deploy/diffs (yang minta {type, path relatif}, bukan path absolut).
const DEPLOY_ROOTS = { frontend: '/awsbasis/dashboard/frontend', backend: '/awsbasis/dashboard/backend', 'ai-service': '/awsbasis/ai-service' };
function absToTypeRel(absPath) {
  for (const [type, root] of Object.entries(DEPLOY_ROOTS)) {
    if (absPath === root) return null;                       // root itu sendiri, bukan file
    if (absPath.startsWith(root + '/')) return { type, rel: absPath.slice(root.length + 1) };
  }
  return null;
}
function downloadTextBlob(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── Modal ringkas: riwayat diff satu file, dipanggil dari File Explorer ──────
// (Mandiri dari BackupPanel — sengaja tidak berbagi state, supaya bisa dibuka
// langsung dari baris file mana pun tanpa pindah tab / isi ulang path+tipe.)
function FileDiffHistoryModal({ api, type, rel, onClose }) {
  const {
    t
  } = useLang();

  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null); // { name, content } sedang dibuka

  useEffect(() => {
    let alive = true;
    setBusy(true);
    api(`/api/file-deploy/diffs?type=${encodeURIComponent(type)}&path=${encodeURIComponent(rel)}`)
      .then((r) => { if (alive) setList(r.diffs || []); })
      .catch(() => { if (alive) setList([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [api, type, rel]);

  const openOne = async (name) => {
    setBusy(true);
    try {
      const r = await api(`/api/file-deploy/diff?type=${encodeURIComponent(type)}&path=${encodeURIComponent(rel)}&name=${encodeURIComponent(name)}`);
      setView({ name, content: r.content });
    } catch (_) { /* diamkan — modal daftar tetap tampil */ }
    finally { setBusy(false); }
  };

  if (view) {
    return (
      <Modal title={t('fdeploy.diff', {
        rel
      })} onClose={onClose} maxWidth={860}>
        <pre style={{
          margin: 0, maxHeight: '60vh', overflow: 'auto', background: '#0f172a', color: '#e2e8f0',
          padding: 14, borderRadius: 8, fontSize: 12, lineHeight: 1.6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        }}>
          {view.content.split('\n').map((ln, i) => {
            let color = '#e2e8f0';
            if (ln.startsWith('+') && !ln.startsWith('+++')) color = '#4ade80';
            else if (ln.startsWith('-') && !ln.startsWith('---')) color = '#f87171';
            else if (ln.startsWith('@@')) color = '#60a5fa';
            else if (ln.startsWith('#')) color = '#94a3b8';
            return <div key={i} style={{ color, whiteSpace: 'pre-wrap' }}>{ln || '\u00A0'}</div>;
          })}
        </pre>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
          <Btn onClick={() => setView(null)}>{t('fdeploy.kembaliDaftar')}</Btn>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={onClose}>{t('fdeploy.tutup')}</Btn>
            <Btn variant="primary" onClick={() => downloadTextBlob(view.content, view.name)}>{t('fdeploy.download')}</Btn>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t('fdeploy.riwayatDiff', {
      rel
    })} onClose={onClose} maxWidth={640}>
      {busy && list === null ? (
        <Empty icon="⏳" text={t('fdeploy.memuatRiwayatDiff')} />
      ) : !list || list.length === 0 ? (
        <Empty icon="📭" text={t('fdeploy.belumAdaRiwayatDiffFile')} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Waktu', 'Oleh', 'Perubahan', ''].map((h, i) => (
                <th key={i} style={{ background: '#f8fafc', padding: '8px 10px', textAlign: i === 3 ? t('fdeploy.right') : t('fdeploy.left'), fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.name}>
                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}>{d.timestamp ? new Date(d.timestamp).toLocaleString('id-ID') : '—'}</td>
                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{d.user || '—'}</td>
                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>+{d.added}</span>{' '}
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>-{d.removed}</span>
                  {d.truncated && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706' }} title={t('fdeploy.fileBerubahBesarDiringkasBukan')}>{t('fdeploy.ringkasan')}</span>}
                </td>
                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                  <Btn size="sm" disabled={busy} onClick={() => openOne(d.name)}>{t('fdeploy.lihat')}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ── FileBrowser (breadcrumb + list, reusable) ───────────────────────────────
const crumbBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--brand-600))', fontFamily: 'inherit', fontSize: 12, padding: 0 };
const rowBtn = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: '8px 12px' };

function FileBrowser({ api, type, onPick, onPickMulti, onClose, multi = false }) {
  const {
    t
  } = useLang();

  const [dir, setDir]   = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const [sel, setSel]   = useState(() => new Set()); // path relatif terpilih (mode multi)

  const load = useCallback(async (d) => {
    setBusy(true); setErr(null);
    try {
      const r = await api(`/api/file-deploy/list?type=${encodeURIComponent(type)}&dir=${encodeURIComponent(d)}`);
      setData(r); setDir(r.dir);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [api, type]);

  useEffect(() => { load(''); }, [load]);

  const toggle = (rel) => setSel((prev) => { const n = new Set(prev); n.has(rel) ? n.delete(rel) : n.add(rel); return n; });

  const segs = dir ? dir.split('/') : [];
  const rootLabel = DEPLOY_ROOTS[type] || DEPLOY_ROOTS.backend;

  return (
    <Modal title={multi ? t('fdeploy.pilihFileBackup') : t('fdeploy.pilihFileServer')} onClose={onClose} maxWidth={560}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 10 }}>
        <button onClick={() => load('')} style={crumbBtn}>{rootLabel}</button>
        {segs.map((s, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <button onClick={() => load(segs.slice(0, i + 1).join('/'))} style={crumbBtn}>{s}</button>
          </span>
        ))}
      </div>
      {err && <Alert msg={err} type="error" onClose={() => setErr(null)} />}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 360, overflow: 'auto' }}>
        {busy ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('fdeploy.memuat')}</div>
        ) : !data || data.entries.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#cbd5e1', fontSize: 13 }}>{t('fdeploy.adaSubfolderFileJsJsx')}</div>
        ) : (
          <>
            {data.parent !== null && (
              <button onClick={() => load(data.parent)} style={rowBtn}><span>📁</span><span style={{ color: '#64748b' }}>..</span></button>
            )}
            {data.entries.map((e) => {
              if (e.type === 'dir') return (
                <button key={e.name} onClick={() => load(dir ? t('fdeploy.teks', {
                  dir,
                  name: e.name
                }) : e.name)} style={rowBtn}>
                  <span>📁</span><span style={{ fontWeight: 500, color: '#334155' }}>{e.name}</span>
                </button>
              );
              const rel = dir ? t('fdeploy.teks', {
                dir,
                name: e.name
              }) : e.name;
              return (
                <button key={e.name} onClick={() => multi ? toggle(rel) : onPick(rel)} style={{ ...rowBtn, background: multi && sel.has(rel) ? 'rgb(var(--brand-50))' : 'none' }}>
                  {multi && <input type="checkbox" readOnly checked={sel.has(rel)} style={{ cursor: 'pointer' }} />}
                  <span>📄</span><span style={{ color: '#1e293b' }}>{e.name}</span>
                  <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 11 }}>{fmtBytes(e.size)}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
      {multi && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>{sel.size} {t('fdeploy.fileTerpilih')}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={onClose}>{t('fdeploy.batal')}</Btn>
            <Btn variant="primary" disabled={sel.size === 0} onClick={() => onPickMulti([...sel])}>{t('fdeploy.backup2')} {sel.size} {t('fdeploy.file')}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Tab 1: Deploy ────────────────────────────────────────────────────────────
function DeployPanel({ api, notify }) {
  const {
    t
  } = useLang();

  const [type, setType]         = useState('backend');
  const [targetPath, setTarget] = useState('');
  const [file, setFile]         = useState(null);
  const [newText, setNewText]   = useState('');     // isi file yang dipilih
  const [serverText, setServer] = useState(null);   // isi file di server (untuk diff)
  const [serverExists, setExists] = useState(false);
  const [diff, setDiff]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [confirm, setConfirm]   = useState(false);
  const [browse, setBrowse]     = useState(false);
  const [log, setLog]           = useState('');
  const [building, setBuilding] = useState(false);
  // ── Auto-locate ──
  // locate = { input, normalized, wasRenamed, matches[], status } | null
  const [locate, setLocate]     = useState(null);
  const [locating, setLocating] = useState(false);
  const inputRef = useRef(null);
  const logRef   = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const reset = () => { setFile(null); setNewText(''); setServer(null); setExists(false); setDiff(null); setLocate(null); if (inputRef.current) inputRef.current.value = ''; };

  // Pilih salah satu kandidat hasil auto-locate → isi type + path sekaligus.
  const applyMatch = (m) => { setType(m.type); setTarget(m.path); setServer(null); setDiff(null); };

  const onPick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!/\.(js|jsx)$/i.test(f.name)) { notify(t('fdeploy.error'), t('fdeploy.hanyaFileJsJsxDiizinkan')); e.target.value = ''; return; }
    setFile(f);
    setNewText(await f.text());
    setServer(null); setDiff(null); setLocate(null);

    // ── Auto-deteksi lokasi file di proyek ──
    // Nama seperti "server(1).js" (hasil unduh berulang) dinormalisasi dulu di
    // server. Bila kandidatnya TEPAT SATU, type & path diisi otomatis. Bila
    // lebih dari satu, sengaja TIDAK diisi — user harus memilih, karena di
    // proyek ini server.js ada di 3 lokasi berbeda.
    setLocating(true);
    try {
      const r = await api(`/api/file-deploy/locate?names=${encodeURIComponent(f.name)}`);
      const info = r?.results?.[0] || null;
      setLocate(info);
      if (info?.status === 'unique') applyMatch(info.matches[0]);
      else if (info?.status === 'new' && !targetPath.trim()) setTarget(info.normalized);
      // Ambigu: sengaja KOSONGKAN path. Tombol deploy ikut terkunci sampai
      // user memilih kandidat — inilah yang mencegah salah timpa.
      else if (info?.status === 'ambiguous') setTarget('');
    } catch (_) {
      // Gagal auto-locate bukan error fatal — user tetap bisa isi path manual.
      if (!targetPath.trim()) setTarget(f.name);
    } finally {
      setLocating(false);
    }
  };

  // Download file AKTIF dari server (reuse /preview → Blob). Hanya .js/.jsx.
  const downloadServer = async () => {
    if (!targetPath.trim()) { notify(t('fdeploy.error'), t('fdeploy.isiDuluPathFileServer')); return; }
    setBusy(true);
    try {
      const r = await api('/api/file-deploy/preview', { method: 'POST', body: JSON.stringify({ type, targetPath }) });
      if (!r.exists) { notify(t('fdeploy.error'), t('fdeploy.fileAdaServer')); return; }
      const blob = new Blob([r.content], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = targetPath.split('/').pop();
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      notify(t('fdeploy.success'), t('fdeploy.diunduh', {
        download: a.download
      }));
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  const loadPreview = async () => {
    if (!targetPath.trim()) { notify(t('fdeploy.error'), t('fdeploy.isiDuluPathTujuanServer')); return; }
    setBusy(true);
    try {
      const r = await api('/api/file-deploy/preview', { method: 'POST', body: JSON.stringify({ type, targetPath }) });
      setServer(r.content || ''); setExists(!!r.exists);
      setDiff(r.exists ? diffLines(r.content || '', newText) : null);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  const doDeploy = async () => {
    setConfirm(false);
    setBusy(true); setLog('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      fd.append('targetPath', targetPath);
      const res = await axiosApi.post('/api/file-deploy/deploy', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      const bk = d.backupName ? t('fdeploy.backup3', {
        backupName: d.backupName
      }) : t('fdeploy.fileBaruTanpaBackup');
      if (d.type === 'frontend') {
        notify(t('fdeploy.success'), t('fdeploy.fileTerDeployMemulaiRebuild', {
          bk
        }));
        await runBuild();
      } else {
        const label = d.type === 'ai-service' ? t('fdeploy.aiService') : t('fdeploy.backend');
        notify(t('fdeploy.warning'), t('fdeploy.fileTerDeploySedangRestart', {
          bk,
          label
        }));
      }
    } catch (e) {
      notify(t('fdeploy.error'), (e.response?.data?.error) || e.message || 'Deploy gagal.');
    } finally { setBusy(false); }
  };

  // Stream `npm run build` via fetch ReadableStream (axios tak cocok untuk stream).
  const runBuild = async () => {
    setBuilding(true);
    setLog('▶ Memulai build…\n');
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/api/file-deploy/build`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) { throw new Error(`HTTP ${resp.status}`); }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        setLog((prev) => prev + dec.decode(value, { stream: true }));
      }
      setLog((prev) => {
        const m = /__BUILD_DONE__ exit=(\d+)/.exec(prev);
        if (m && m[1] === '0') notify(t('fdeploy.success'), t('fdeploy.buildSelesaiHardRefreshCtrl'));
        else if (m) notify(t('fdeploy.error'), `Build gagal (exit ${m[1]}). Cek log di bawah.`);
        return prev;
      });
    } catch (e) {
      setLog((prev) => prev + `\n__BUILD_ERROR__ ${e.message}\n`);
      notify(t('fdeploy.error'), 'Build gagal: ' + e.message);
    } finally { setBuilding(false); }
  };

  const canDeploy = file && targetPath.trim() && !busy && !building;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 14, marginBottom: 14 }}>
        <Field label={t('fdeploy.tipe')}>
          <Select value={type} onChange={(e) => { setType(e.target.value); setServer(null); setDiff(null); }}>
            <option value="backend">{t('fdeploy.backend')}</option>
            <option value="frontend">{t('fdeploy.frontend')}</option>
            <option value="ai-service">{t('fdeploy.aiService')}</option>
          </Select>
        </Field>
        <Field label={t('fdeploy.pathTujuanServer')} hint={t(TYPE_HINT[type])}>
          <Input value={targetPath} placeholder={type === 'frontend' ? 'src/pages/NamaFile.jsx' : t('fdeploy.namafileJs')} onChange={(e) => { setTarget(e.target.value); setDiff(null); setServer(null); }} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input ref={inputRef} type="file" accept=".js,.jsx" style={{ display: 'none' }} onChange={onPick} />
        <Btn variant="primary" size="sm" disabled={busy || building} onClick={() => inputRef.current && inputRef.current.click()}>{t('fdeploy.pilihFile')}</Btn>
        <Btn size="sm" disabled={busy || building} onClick={downloadServer}>{t('fdeploy.downloadServer')}</Btn>
        <Btn size="sm" disabled={busy || building} onClick={() => setBrowse(true)}>{t('fdeploy.browseServer')}</Btn>
        {file && <Btn size="sm" disabled={busy || building} onClick={loadPreview}>🔍 {busy ? t('fdeploy.memuat2') : t('fdeploy.lihatDiff')}</Btn>}
        {file && <Btn size="sm" disabled={busy || building} onClick={reset}>{t('fdeploy.bersihkan')}</Btn>}
        {file && <span style={{ fontSize: 12, color: '#475569', alignSelf: 'center' }}>📎 <b>{file.name}</b> · {fmtBytes(file.size)}</span>}
      </div>

      {/* ── Hasil auto-locate ──────────────────────────────────────────────
          unique    → sudah terisi otomatis, cukup diberi tahu ke mana
          ambiguous → user WAJIB memilih; tidak ada yang dipilih otomatis
          new       → file belum ada di proyek, path diisi manual            */}
      {locating && (
        <div style={S.locateBox('#f8fafc', '#e2e8f0')}>{t('fdeploy.locSearching')}</div>
      )}
      {!locating && locate && locate.wasRenamed && (
        <div style={S.locateBox('rgb(var(--brand-50))', '#bfdbfe')}>
          {t('fdeploy.locRenamed', { from: locate.input, to: locate.normalized })}
        </div>
      )}
      {!locating && locate?.status === 'unique' && (
        <div style={S.locateBox('#ecfdf5', '#a7f3d0')}>
          ✅ {t('fdeploy.locFound')} <b>[{locate.matches[0].type}]</b>{' '}
          <code style={S.locateCode}>{locate.matches[0].path}</code>
        </div>
      )}
      {!locating && locate?.status === 'new' && (
        <div style={S.locateBox('#fffbeb', '#fde68a')}>
          ℹ️ {t('fdeploy.locNew', { name: locate.normalized })}
        </div>
      )}
      {!locating && locate?.status === 'ambiguous' && (
        <div style={S.locateBox('#fff7ed', '#fed7aa')}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ⚠️ {t('fdeploy.locAmbiguous', { n: locate.matches.length, name: locate.normalized })}
          </div>
          <div style={{ marginBottom: 8, color: '#7c2d12' }}>
            {t('fdeploy.locChooseHint')}
            {file && <> · {t('fdeploy.locUploadSize', { size: fmtBytes(file.size) })}</>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {locate.matches.map((m, i) => {
              const picked = m.type === type && m.path === targetPath;
              // Selisih terhadap file yang diunggah — alat verifikasi, bukan
              // penentu (lihat catatan yang sama di Multi Deploy).
              const delta = file ? file.size - m.size : null;
              const same  = delta === 0;
              return (
                <button key={i} onClick={() => applyMatch(m)} style={S.locateOption(picked)}>
                  <span style={{ fontWeight: 700, marginRight: 8 }}>[{m.type}]</span>
                  <code style={{ fontSize: 12 }}>{m.path}</code>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                    {fmtBytes(m.size)}
                    {delta !== null && (
                      <> · <b style={{ color: same ? '#16a34a' : '#b45309' }}>
                        {same ? t('fdeploy.locSizeSame') : (delta > 0 ? '+' : '−') + fmtBytes(Math.abs(delta))}
                      </b></>
                    )}
                    {' · '}
                    {fmtIsoWib(new Date(m.mtime).toISOString(), false)}
                  </span>
                  {picked && <span style={{ marginLeft: 8, color: '#16a34a', fontWeight: 700 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Ringkasan diff */}
      {file && serverText !== null && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          {!serverExists ? (
            <div style={{ fontSize: 13, color: 'rgb(var(--brand-600))' }}>{t('fdeploy.fileBelumAdaServerDibuat')}</div>
          ) : diff === null ? (
            <div style={{ fontSize: 13, color: '#64748b' }}>{t('fdeploy.fileTerlaluBesarDitampilkanDiff')} {fmtBytes(serverText.length)} {t('fdeploy.baru')} {fmtBytes(newText.length)}.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>
                {t('fdeploy.diff2')} <span style={{ color: '#16a34a' }}>+{diff.filter(d => d.type === 'add').length}</span>{' '}
                <span style={{ color: '#dc2626' }}>−{diff.filter(d => d.type === 'del').length}</span>{' '}
                <span style={{ color: '#94a3b8' }}>({diff.filter(d => d.type === 'eq').length} {t('fdeploy.sama')}</span>
              </div>
              <pre style={{ margin: 0, maxHeight: 320, overflow: 'auto', fontSize: 12, lineHeight: 1.5, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                {diff.map((d, i) => (
                  <div key={i} style={{
                    background: d.type === 'add' ? '#ecfdf5' : d.type === 'del' ? '#fef2f2' : 'transparent',
                    color: d.type === 'add' ? '#065f46' : d.type === 'del' ? '#7f1d1d' : '#334155',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    <span style={{ userSelect: 'none', color: '#cbd5e1', marginRight: 8 }}>{d.type === 'add' ? '+' : d.type === 'del' ? '−' : ' '}</span>
                    {d.text || ' '}
                  </div>
                ))}
              </pre>
            </>
          )}
        </div>
      )}
      <Btn variant="primary" disabled={!canDeploy} onClick={() => setConfirm(true)}>
        🚀 {type === 'frontend' ? t('fdeploy.deployRebuildFrontend') : type === 'ai-service' ? t('fdeploy.deployRestartAiService') : t('fdeploy.deployRestartBackend')}
      </Btn>
      {/* Log build */}
      {(log || building) && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
            {building ? t('fdeploy.buildBerjalan') : t('fdeploy.logBuild')}
          </div>
          <pre ref={logRef} style={{ margin: 0, maxHeight: 360, overflow: 'auto', fontSize: 12, lineHeight: 1.45, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {log || ' '}
          </pre>
        </div>
      )}
      {browse && (
        <FileBrowser api={api} type={type}
          onPick={(p) => { setTarget(p); setBrowse(false); setDiff(null); setServer(null); }}
          onClose={() => setBrowse(false)} />
      )}
      {confirm && (
        <Modal title={t('fdeploy.konfirmasiDeploy')} onClose={() => setConfirm(false)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}><Tx text={t('fdeploy.confirmDeployOne', { name: file?.name, type })} code={targetPath} />
                                    <br /><br />
            {t('fdeploy.fileLamaBilaAdaBackup')}{' '}
            {type === 'frontend' ? t('fdeploy.frontendRebuildBeberapaMenit') : type === 'ai-service' ? t('fdeploy.aiServiceRestartSetelahnya') : t('fdeploy.backendRestartSetelahnya')}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirm(false)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="primary" onClick={doDeploy}>{t('fdeploy.yaDeploy')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 2: Backup & Restore ─────────────────────────────────────────────────
// ── Scheduled Backups (crontab ec2-user → /backup/awsbasis) ─────────────────────
// Backend: backend/scheduledBackups.js (/api/sched-backup/*, admin-only). Teks literal Inggris.
const sbFmtBytes = (n) => { if (n == null) return '—'; const u = ['B', 'KB', 'MB', 'GB', 'TB']; let i = 0; let v = Number(n); while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; } return v.toFixed(i ? 1 : 0) + ' ' + u[i]; };
const sbFmtTime = (s) => { if (!s) return '—'; const d = new Date(s.replace(/^(\d{4}-\d\d-\d\d) /, '$1T').replace(/ ([+-]\d\d)(\d\d)$/, '$1:$2')); return isNaN(d) ? s : d.toLocaleString(); };
function sbDescribeCron(expr) {
  if (!expr) return 'not scheduled';
  const [m, h, dom, mon, dow] = expr.split(' ');
  const hhmm = /^\d+$/.test(h) && /^\d+$/.test(m) ? String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') : null;
  if (hhmm && dom === '*' && mon === '*' && dow === '*') return 'Daily at ' + hhmm;
  if (hhmm && dom === '*' && mon === '*' && /^\d$/.test(dow)) return 'Weekly (' + ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(dow)] + ') at ' + hhmm;
  return expr;
}
function ScheduledBackups({ api, notify }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);   // {id, label, schedule, retention, enabled}
  const [logView, setLogView] = useState(null); // {label, log, cron}
  const [filesView, setFilesView] = useState(null); // {job, files}
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api('/api/sched-backup/status')); }
    catch (e) { notify('Error', e.message); }
    finally { setLoading(false); }
  }, [api, notify]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data || !data.jobs.some(j => j.running)) return undefined;
    const id = setTimeout(load, 5000); return () => clearTimeout(id);
  }, [data, load]);

  const runNow = async (j) => {
    try { await api('/api/sched-backup/' + j.id + '/run', { method: 'POST', body: '{}' }); notify('Started', j.label + ' backup is running in the background.'); load(); }
    catch (e) { notify('Error', e.message); }
  };
  const save = async () => {
    try {
      await api('/api/sched-backup/' + edit.id, { method: 'PUT', body: JSON.stringify({ schedule: edit.schedule, retention: Number(edit.retention), enabled: edit.enabled }) });
      notify('Saved', edit.label + ': schedule and retention updated.'); setEdit(null); load();
    } catch (e) { notify('Error', e.message); }
  };
  const showLog = async (j) => {
    try { const r = await api('/api/sched-backup/' + j.id + '/log?lines=300'); setLogView({ label: j.label, ...r }); }
    catch (e) { notify('Error', e.message); }
  };
  const showFiles = async (j) => {
    try { const r = await api('/api/sched-backup/' + j.id + '/files'); setFilesView({ job: j, files: r.files }); }
    catch (e) { notify('Error', e.message); }
  };
  const download = async (j, f) => {
    try {
      notify('Downloading', f.name + ' (' + sbFmtBytes(f.size) + ')…');
      const res = await axiosApi.get('/api/sched-backup/' + j.id + '/download/' + encodeURIComponent(f.name), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a');
      a.href = url; a.download = f.name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { notify('Error', e.message); }
  };

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
  const disk = data && data.disk;
  const pct = disk ? Math.round((disk.used / disk.size) * 100) : 0;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 22, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: open ? '1px solid #e2e8f0' : 'none', flexWrap: 'wrap' }}>
        <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#0f172a', padding: 0, fontFamily: 'inherit' }}>
          {open ? '▾' : '▸'} ⏰ Scheduled Backups
        </button>
        <span style={{ fontSize: 12, color: '#64748b' }}>crontab ec2-user → /backup/awsbasis</span>
        <div style={{ flex: 1 }} />
        {disk && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
            <span>{disk.source}</span>
            <div style={{ width: 110, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: pct > 85 ? '#dc2626' : pct > 70 ? '#d97706' : '#16a34a' }} />
            </div>
            <span>{sbFmtBytes(disk.used)} / {sbFmtBytes(disk.size)} ({pct}%)</span>
          </div>
        )}
        <Btn size="sm" onClick={load} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</Btn>
      </div>
      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Job</th><th style={th}>Schedule</th><th style={th}>Retention</th>
              <th style={th}>Last run</th><th style={th}>Copies</th><th style={th}>Latest file</th><th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr></thead>
            <tbody>
              {!data && <tr><td style={td} colSpan={7}>Loading…</td></tr>}
              {data && data.jobs.map(j => (
                <tr key={j.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{j.label}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{j.dir}</div>
                  </td>
                  <td style={td}>
                    <div>{sbDescribeCron(j.schedule)}</div>
                    <div style={{ marginTop: 3 }}>{j.enabled ? <Badge text="Enabled" color="#16a34a" /> : <Badge text="Disabled" color="#94a3b8" />} <code style={{ fontSize: 11, color: '#94a3b8' }}>{j.schedule || ''}</code></div>
                  </td>
                  <td style={td}>{j.retention != null ? j.retention + ' days' : '—'}</td>
                  <td style={td}>
                    {j.running ? <Badge text="Running…" color="#2563eb" />
                      : j.last ? <Badge text={j.last.status} color={j.last.status === 'OK' ? '#16a34a' : '#dc2626'} /> : <Badge text="No runs" color="#94a3b8" />}
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{j.running ? 'started ' + sbFmtTime(j.running) : j.last ? sbFmtTime(j.last.at) : ''}</div>
                  </td>
                  <td style={td}>
                    <div>{j.count}{j.badCount ? <span style={{ color: '#dc2626' }}> (+{j.badCount} bad)</span> : null}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{sbFmtBytes(j.totalSize)}</div>
                  </td>
                  <td style={td}>
                    {j.latest ? <><div style={{ fontFamily: 'monospace', fontSize: 12 }}>{j.latest.name}</div><div style={{ fontSize: 11, color: '#64748b' }}>{sbFmtBytes(j.latest.size)}</div></> : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <Btn size="sm" variant="primary" disabled={!!j.running} onClick={() => runNow(j)}>▶ Run now</Btn>
                      <Btn size="sm" onClick={() => showFiles(j)}>Files</Btn>
                      <Btn size="sm" onClick={() => showLog(j)}>Log</Btn>
                      <Btn size="sm" onClick={() => setEdit({ id: j.id, label: j.label, schedule: j.schedule || '0 1 * * *', retention: j.retention || 7, enabled: j.enabled })}>⚙ Edit</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <Modal title={'Edit schedule — ' + edit.label} onClose={() => setEdit(null)}>
          <Field label="Cron schedule (server time)" hint={'minute hour day month weekday — e.g. "0 1 * * *" = daily 01:00 · ' + sbDescribeCron(edit.schedule.trim().replace(/\s+/g, ' '))}>
            <Input value={edit.schedule} onChange={e => setEdit({ ...edit, schedule: e.target.value })} style={{ fontFamily: 'monospace' }} />
          </Field>
          <Field label="Retention (days)" hint="Backups older than this are deleted after each run.">
            <Input type="number" min={1} max={365} value={edit.retention} onChange={e => setEdit({ ...edit, retention: e.target.value })} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '4px 0 16px' }}>
            <input type="checkbox" checked={edit.enabled} onChange={e => setEdit({ ...edit, enabled: e.target.checked })} /> Enabled
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn onClick={() => setEdit(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}

      {logView && (
        <Modal title={'Backup log — ' + logView.label} onClose={() => setLogView(null)} maxWidth={900}>
          <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, fontSize: 11.5, maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
            {(logView.log || []).length ? logView.log.join('\n') : '(empty)'}
          </pre>
          {(logView.cron || []).length > 0 && (<>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '12px 0 6px' }}>Cron stdout/stderr</div>
            <pre style={{ background: '#f8fafc', padding: 10, borderRadius: 8, fontSize: 11.5, maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>{logView.cron.join('\n')}</pre>
          </>)}
        </Modal>
      )}

      {filesView && (
        <Modal title={'Backup files — ' + filesView.job.label} onClose={() => setFilesView(null)} maxWidth={720}>
          {filesView.files.length === 0 ? <Empty icon="🗂️" text="No backup files yet." /> : (
            <div style={{ maxHeight: 420, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>File</th><th style={th}>Size</th><th style={th}>Modified</th><th style={th} /></tr></thead>
                <tbody>{filesView.files.map(f => (
                  <tr key={f.name}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{f.name} {f.bad && <Badge text="BAD" color="#dc2626" />}</td>
                    <td style={td}>{sbFmtBytes(f.size)}</td>
                    <td style={td}>{new Date(f.mtime).toLocaleString()}</td>
                    <td style={{ ...td, textAlign: 'right' }}><Btn size="sm" onClick={() => download(filesView.job, f)}>⬇ Download</Btn></td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function BackupPanel({ api, notify }) {
  const {
    t
  } = useLang();

  const [type, setType]         = useState('backend');
  const [targetPath, setTarget] = useState('');
  const [list, setList]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [confirm, setConfirm]   = useState(null); // backupName yang akan dipulihkan
  const [browse, setBrowse]     = useState(false);
  const [browseMulti, setBrowseMulti] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  // Riwayat diff (tracing) — dari deploy via tab Deploy, path/type sama dgn backup di atas.
  const [diffList, setDiffList] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [viewDiff, setViewDiff] = useState(null); // { name, content } sedang dilihat

  const backupNow = async (paths) => {
    setBrowseMulti(false); setBusy(true);
    try {
      const r = await api('/api/file-deploy/backup-now', { method: 'POST', body: JSON.stringify({ type, paths }) });
      const failed = (r.results || []).filter((x) => !x.ok);
      notify(r.okCount === r.total ? t('fdeploy.success') : t('fdeploy.warning'),
        `Backup selesai: ${r.okCount}/${r.total} file.` + (failed.length ? ` Gagal: ${failed.map((f) => f.path).join(', ')}` : ''));
      if (targetPath && paths.includes(targetPath)) load();
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  const backupAll = async () => {
    setConfirmAll(false); setBusy(true);
    try {
      const r = await api('/api/file-deploy/backup-all', { method: 'POST', body: JSON.stringify({ type }) });
      notify(r.fail === 0 ? t('fdeploy.success') : t('fdeploy.warning'), `Backup SEMUA (${type}): ${r.ok}/${r.total} file ter-backup${r.fail ? t('fdeploy.gagal', {
        fail: r.fail
      }) : ''}.`);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  const load = async () => {
    if (!targetPath.trim()) { notify(t('fdeploy.error'), t('fdeploy.isiPathFileDulu')); return; }
    setBusy(true);
    try {
      const r = await api(`/api/file-deploy/backups?type=${encodeURIComponent(type)}&path=${encodeURIComponent(targetPath)}`);
      setList(r.backups || []);
    } catch (e) { notify(t('fdeploy.error'), e.message); setList(null); }
    finally { setBusy(false); }
    loadDiffs();
  };

  // Riwayat diff: hanya dari deploy via tab Deploy (deploy / deploy-multi). Path
  // & tipe sama dgn field backup di atas, jadi dipanggil sekalian saat "Cari backup".
  const loadDiffs = async () => {
    if (!targetPath.trim()) return;
    setDiffBusy(true);
    try {
      const r = await api(`/api/file-deploy/diffs?type=${encodeURIComponent(type)}&path=${encodeURIComponent(targetPath)}`);
      setDiffList(r.diffs || []);
    } catch (e) { setDiffList(null); }
    finally { setDiffBusy(false); }
  };

  const openDiff = async (name) => {
    setDiffBusy(true);
    try {
      const r = await api(`/api/file-deploy/diff?type=${encodeURIComponent(type)}&path=${encodeURIComponent(targetPath)}&name=${encodeURIComponent(name)}`);
      setViewDiff({ name, content: r.content });
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setDiffBusy(false); }
  };

  const doRestore = async () => {
    const backupName = confirm; setConfirm(null);
    setBusy(true);
    try {
      const r = await api('/api/file-deploy/restore', { method: 'POST', body: JSON.stringify({ type, targetPath, backupName }) });
      if (r.type === 'frontend') notify(t('fdeploy.success'), t('fdeploy.dipulihkanJalankanRebuildTabDeploy', {
        backupName
      }));
      else { const label = r.type === 'ai-service' ? t('fdeploy.aiService') : t('fdeploy.backend'); notify(t('fdeploy.warning'), t('fdeploy.dipulihkanRestartTunggu10Detik', {
        backupName,
        label
      })); }
      load();
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <ScheduledBackups api={api} notify={notify} />
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 14, alignItems: 'end', marginBottom: 14 }}>
        <Field label={t('fdeploy.tipe')}>
          <Select value={type} onChange={(e) => { setType(e.target.value); setList(null); setDiffList(null); }}>
            <option value="backend">{t('fdeploy.backend')}</option>
            <option value="frontend">{t('fdeploy.frontend')}</option>
            <option value="ai-service">{t('fdeploy.aiService')}</option>
          </Select>
        </Field>
        <Field label={t('fdeploy.pathFile')} hint={t(TYPE_HINT[type])}>
          <Input value={targetPath} placeholder={type === 'frontend' ? 'src/pages/NamaFile.jsx' : t('fdeploy.namafileJs')} onChange={(e) => { setTarget(e.target.value); setList(null); setDiffList(null); }} />
        </Field>
        <div style={{ marginBottom: 12, display: 'flex', gap: 6 }}>
          <Btn disabled={busy} onClick={() => setBrowse(true)}>{t('fdeploy.browse')}</Btn>
          <Btn disabled={busy} onClick={() => setBrowseMulti(true)}>{t('fdeploy.backupSekarang')}</Btn>
          <Btn disabled={busy} onClick={() => setConfirmAll(true)}>{t('fdeploy.backupSemua')}</Btn>
          <Btn variant="primary" disabled={busy} onClick={load}>🔍 {busy ? t('fdeploy.memuat2') : t('fdeploy.cariBackup')}</Btn>
        </div>
      </div>
      {list === null ? (
        <Empty icon="🗂️" text={t('fdeploy.masukkanPathFileLaluKlik')} />
      ) : list.length === 0 ? (
        <Empty icon="📭" text={t('fdeploy.belumAdaBackupFile')} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Waktu backup', 'Ukuran', 'Nama berkas', ''].map((h, i) => (
                <th key={i} style={{ background: '#f8fafc', padding: '9px 12px', textAlign: i === 3 ? t('fdeploy.right') : t('fdeploy.left'), fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.name}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}>{fmtTs(b.timestamp)}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{fmtBytes(b.size)}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>{b.name}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                  <Btn size="sm" variant="warning" disabled={busy} onClick={() => setConfirm(b.name)}>{t('fdeploy.pulihkan')}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* ── Riwayat Diff (tracing) ────────────────────────────────────────────
          Hanya terisi dari deploy via tab Deploy/Multi Deploy (bukan dari
          "Backup sekarang" — itu snapshot manual tanpa perbandingan). */}
      {list !== null && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#334155', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('fdeploy.riwayatDiffDeploy')}</h3>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('fdeploy.tracingApaBerubahTiapDeploy')}</span>
          </div>
          {diffBusy && diffList === null ? (
            <Empty icon="⏳" text={t('fdeploy.memuatRiwayatDiff')} />
          ) : !diffList || diffList.length === 0 ? (
            <Empty icon="📭" text={t('fdeploy.belumAdaRiwayatDiffFile')} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Waktu deploy', 'Oleh', 'Perubahan', ''].map((h, i) => (
                    <th key={i} style={{ background: '#f8fafc', padding: '9px 12px', textAlign: i === 3 ? t('fdeploy.right') : t('fdeploy.left'), fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diffList.map((d) => (
                  <tr key={d.name}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}>{d.timestamp ? new Date(d.timestamp).toLocaleString('id-ID') : '—'}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{d.user || '—'}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>+{d.added}</span>{' '}
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>-{d.removed}</span>
                      {d.truncated && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706' }} title={t('fdeploy.fileBerubahBesarDiringkasBukan')}>{t('fdeploy.ringkasan')}</span>}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                      <Btn size="sm" disabled={diffBusy} onClick={() => openDiff(d.name)}>{t('fdeploy.lihatDiff2')}</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {viewDiff && (
        <Modal title={t('fdeploy.diff3', {
          targetPath
        })} onClose={() => setViewDiff(null)} maxWidth={860}>
          <pre style={{
            margin: 0, maxHeight: '60vh', overflow: 'auto', background: '#0f172a', color: '#e2e8f0',
            padding: 14, borderRadius: 8, fontSize: 12, lineHeight: 1.6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}>
            {viewDiff.content.split('\n').map((ln, i) => {
              let color = '#e2e8f0';
              if (ln.startsWith('+') && !ln.startsWith('+++')) color = '#4ade80';
              else if (ln.startsWith('-') && !ln.startsWith('---')) color = '#f87171';
              else if (ln.startsWith('@@')) color = '#60a5fa';
              else if (ln.startsWith('#')) color = '#94a3b8';
              return <div key={i} style={{ color, whiteSpace: 'pre-wrap' }}>{ln || '\u00A0'}</div>;
            })}
          </pre>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <Btn onClick={() => setViewDiff(null)}>{t('fdeploy.tutup')}</Btn>
            <Btn variant="primary" onClick={() => {
              const blob = new Blob([viewDiff.content], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = viewDiff.name; document.body.appendChild(a); a.click();
              document.body.removeChild(a); URL.revokeObjectURL(url);
            }}>
              {t('fdeploy.downloadDiffTxt')}
            </Btn>
          </div>
        </Modal>
      )}
      {browse && (
        <FileBrowser api={api} type={type}
          onPick={(p) => { setTarget(p); setBrowse(false); setList(null); setDiffList(null); }}
          onClose={() => setBrowse(false)} />
      )}
      {browseMulti && (
        <FileBrowser api={api} type={type} multi
          onPickMulti={backupNow}
          onClose={() => setBrowseMulti(false)} />
      )}
      {confirmAll && (
        <Modal title={t('fdeploy.backupSemuaFile')} onClose={() => setConfirmAll(false)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            <Tx text={t('fdeploy.confirmBackupAll', { type })} code={`/dashboard/${type}`} />
            <br /><br />
            {t('fdeploy.folderBeratNodeModulesBuild')}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirmAll(false)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="primary" onClick={backupAll}>{t('fdeploy.yaBackupSemua')}</Btn>
          </div>
        </Modal>
      )}
      {confirm && (
        <Modal title={t('fdeploy.konfirmasiRestore')} onClose={() => setConfirm(null)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}><Tx text={t('fdeploy.confirmRestore')} code={targetPath} b={fmtTs(/@@(.+)\.bak$/.exec(confirm)?.[1])} />
                                    <br /><br />
            {t('fdeploy.kondisiFileSaatBackupDulu')}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirm(null)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="warning" onClick={doRestore}>{t('fdeploy.yaPulihkan')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Multi Deploy (banyak file, path per file, tanpa diff) ───────────────
function MultiDeployPanel({ api, notify }) {
  const {
    t
  } = useLang();

  // `type` di sini adalah TIPE DEFAULT untuk baris baru saja. Sejak dukungan
  // lintas-tipe, tipe sesungguhnya disimpan PER BARIS (rows[i].type) sehingga
  // satu batch boleh mencampur frontend + backend + ai-service.
  const [type, setType]   = useState('backend');
  const [rows, setRows]   = useState([]); // [{ file, path, type, loc }]
  const [busy, setBusy]   = useState(false);
  const [building, setBuilding] = useState(false);
  const [confirm, setConfirm]   = useState(false);
  const [log, setLog]     = useState('');
  const [locating, setLocating] = useState(false);
  const inputRef = useRef(null);
  const logRef   = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = picked.filter((f) => /\.(js|jsx)$/i.test(f.name));
    if (valid.length !== picked.length) notify(t('fdeploy.warning'), t('fdeploy.sebagianFileDilewatiHanyaJs'));
    e.target.value = '';
    if (!valid.length) return;

    // Tambahkan dulu dengan path sementara supaya daftar langsung terlihat.
    const baseIdx = rows.length;
    setRows((prev) => [...prev, ...valid.map((f) => ({ file: f, path: f.name, type, loc: null }))]);

    // ── Auto-deteksi lokasi untuk semua file sekaligus (satu request) ──
    setLocating(true);
    try {
      const names = valid.map((f) => f.name).join(',');
      const r = await api(`/api/file-deploy/locate?names=${encodeURIComponent(names)}`);
      const results = r?.results || [];
      setRows((prev) => prev.map((row, idx) => {
        const k = idx - baseIdx;
        if (k < 0 || k >= results.length) return row;
        const info = results[k];
        // Hanya isi otomatis bila kandidatnya TEPAT SATU. Ambigu → dikosongkan
        // supaya barisnya jelas butuh perhatian dan deploy ikut terkunci.
        const path = info.status === 'unique' ? info.matches[0].path
                   : info.status === 'new'    ? info.normalized
                   : '';
        // Tipe ikut hasil deteksi — inilah yang membuat batch campuran aman:
        // file mendarat di root tempat ia benar-benar ditemukan, bukan di root
        // yang kebetulan sedang terpilih di dropdown.
        const rowType = info.status === 'unique' ? info.matches[0].type : row.type;
        return { ...row, path, type: rowType, loc: info };
      }));
    } catch (_) {
      // Auto-locate gagal → biarkan path = nama file, user isi manual.
    } finally {
      setLocating(false);
    }
  };

  // Terapkan kandidat pilihan user pada satu baris — path DAN tipe sekaligus.
  const applyRowMatch = (i, m) =>
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, path: m.path, type: m.type } : r));

  const setRowType = (i, v) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, type: v } : r));

  // Komposisi batch per tipe — dipakai untuk ringkasan, label tombol, dan
  // penentuan langkah sesudah tulis (build / restart).
  const counts = rows.reduce((acc, r) => { acc[r.type] = (acc[r.type] || 0) + 1; return acc; }, {});
  const needBuild = (counts.frontend || 0) > 0;
  // Urutan WAJIB: ai-service dulu, backend terakhir (restart backend memutus
  // koneksi ke API sehingga langkah setelahnya tidak akan terkirim).
  const pendingRestart = ['ai-service', 'backend'].filter((tp) => (counts[tp] || 0) > 0);

  const setPath = (i, v) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, path: v } : r));
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  // Mengembalikan true bila build selesai dengan exit 0. Nilainya dipakai
  // doDeploy untuk memutuskan apakah restart pm2 boleh dilanjutkan.
  const runBuild = async () => {
    setBuilding(true); setLog('▶ Memulai build…\n');
    let ok = false;
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/api/file-deploy/build`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        acc += chunk;
        setLog((p) => p + chunk);
      }
      const m = /__BUILD_DONE__ exit=(\d+)/.exec(acc);
      ok = Boolean(m && m[1] === '0');
      if (ok) notify(t('fdeploy.success'), t('fdeploy.buildSelesaiHardRefreshCtrl'));
      else if (m) notify(t('fdeploy.error'), `Build gagal (exit ${m[1]}). Cek log.`);
    } catch (e) { setLog((p) => p + `\n__BUILD_ERROR__ ${e.message}\n`); notify(t('fdeploy.error'), 'Build gagal: ' + e.message); }
    finally { setBuilding(false); }
    return ok;
  };

  const doDeploy = async () => {
    setConfirm(false); setBusy(true); setLog('');
    try {
      const fd = new FormData();
      rows.forEach((r) => fd.append('files', r.file));
      // Kontrak baru: tipe menempel pada tiap file, bukan pada batch.
      fd.append('items', JSON.stringify(rows.map((r) => ({ type: r.type, path: r.path }))));
      const res = await axiosApi.post('/api/file-deploy/deploy-multi', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      const failed = (d.results || []).filter((x) => !x.ok);
      const base = `${d.okCount}/${d.total} file ter-deploy` + (failed.length ? `. Gagal: ${failed.map((f) => f.path).join(', ')}` : '');

      if (!d.okCount) { notify(t('fdeploy.error'), base); return; }

      // ── Langkah 2: build frontend lebih dulu (kalau ada file frontend) ──
      // Build DULU, restart pm2 KEMUDIAN. Membalik urutannya berarti restart
      // berjalan saat build masih memakai CPU/memori — itu yang dilarang runbook.
      if (d.needBuild) {
        notify(failed.length ? t('fdeploy.warning') : t('fdeploy.success'), t('fdeploy.memulaiRebuildFrontend', { base }));
        const buildOk = await runBuild();
        if (!buildOk && (d.pendingRestart || []).length) {
          // File sudah tertulis di server, tapi restart ditahan supaya tidak
          // menumpuk kegagalan. User bisa restart manual dari tab Operasi.
          notify(t('fdeploy.error'), t('fdeploy.mdBuildFailedNoRestart'));
          return;
        }
      }

      // ── Langkah 3: restart pm2, ai-service dulu, backend terakhir ──
      const order = d.pendingRestart || [];
      for (const tp of order) {
        const label = tp === 'ai-service' ? t('fdeploy.aiService') : t('fdeploy.backend');
        setLog((p) => p + `\n▶ Restart ${label}…\n`);
        try {
          await axiosApi.post('/api/file-deploy/restart', { type: tp });
          notify(t('fdeploy.warning'), t('fdeploy.mdRestarting', { label }));
        } catch (e) {
          // Restart backend memutus koneksi ke API; error di sini normal untuk
          // langkah TERAKHIR, jadi tidak diperlakukan sebagai kegagalan fatal.
          setLog((p) => p + `  (koneksi terputus — normal bila ini restart terakhir)\n`);
        }
        // Beri jeda antar restart supaya pm2 tidak menerima dua perintah
        // delete/start yang saling tumpang tindih.
        if (order.length > 1) await new Promise((r) => setTimeout(r, 2500));
      }

      if (!d.needBuild && order.length === 0) notify(t('fdeploy.success'), base);
    } catch (e) { notify(t('fdeploy.error'), (e.response?.data?.error) || e.message || 'Deploy gagal.'); }
    finally { setBusy(false); }
  };

  const canDeploy = rows.length > 0 && rows.every((r) => r.path.trim() && r.type) && !busy && !building;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 14, marginBottom: 14 }}>
        <Field label={t('fdeploy.mdTypeDefault')} hint={t('fdeploy.mdTypeDefaultHint')}>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="backend">{t('fdeploy.backend')}</option>
            <option value="frontend">{t('fdeploy.frontend')}</option>
            <option value="ai-service">{t('fdeploy.aiService')}</option>
          </Select>
        </Field>
        <Field label={t('fdeploy.file2')} hint={t(TYPE_HINT[type])}>
          <input ref={inputRef} type="file" accept=".js,.jsx" multiple style={{ display: 'none' }} onChange={onPick} />
          <Btn variant="primary" size="sm" disabled={busy || building} onClick={() => inputRef.current && inputRef.current.click()}>{t('fdeploy.tambahFile')}</Btn>
        </Field>
      </div>
      {rows.length === 0 ? (
        <Empty icon="📋" text={t('fdeploy.belumAdaFileKlikTambah')} />
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          {rows.map((r, i) => {
            const st = r.loc?.status;
            return (
            <div key={i} style={{ padding: '10px 12px', borderBottom: i < rows.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#475569', minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {r.file.name} <span style={{ color: '#94a3b8' }}>· {fmtBytes(r.file.size)}</span></span>
                {/* Tipe per baris — inilah yang memungkinkan satu batch campuran. */}
                <Select value={r.type} title={t('fdeploy.mdRowTypeTitle')} disabled={busy || building}
                  onChange={(e) => setRowType(i, e.target.value)}
                  style={{ width: 'auto', fontSize: 12, padding: '4px 6px' }}>
                  <option value="backend">backend</option>
                  <option value="frontend">frontend</option>
                  <option value="ai-service">ai-service</option>
                </Select>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>→</span>
                <Input value={r.path} placeholder={r.type === 'frontend' ? 'src/pages/NamaFile.jsx' : t('fdeploy.namafileJs')} onChange={(e) => setPath(i, e.target.value)} style={{ flex: 1 }} />
                {st === 'unique' && <span title={t('fdeploy.locFound')} style={{ color: '#16a34a', fontSize: 13 }}>✅</span>}
                {st === 'new' && <span title={t('fdeploy.locNewShort')} style={{ color: '#d97706', fontSize: 13 }}>ℹ️</span>}
                {st === 'ambiguous' && <span title={t('fdeploy.locChooseHint')} style={{ color: '#ea580c', fontSize: 13 }}>⚠️</span>}
                <Btn size="sm" variant="danger" disabled={busy || building} onClick={() => removeRow(i)}>✖</Btn>
              </div>
              {r.loc?.wasRenamed && (
                <div style={{ fontSize: 11, color: 'rgb(var(--brand-600))', marginTop: 4, marginLeft: 170 }}>
                  {t('fdeploy.locRenamed', { from: r.loc.input, to: r.loc.normalized })}
                </div>
              )}
              {st === 'ambiguous' && (
                <div style={{ marginTop: 6, marginLeft: 170, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#9a3412' }}>
                    {t('fdeploy.locAmbiguousShort', { n: r.loc.matches.length })}
                    {' · '}
                    {t('fdeploy.locUploadSize', { size: fmtBytes(r.file.size) })}
                  </span>
                  {r.loc.matches.map((m, k) => {
                    // Selisih ukuran terhadap file yang diunggah. Ini alat
                    // verifikasi, BUKAN penentu: ukuran sama tidak menjamin file
                    // yang benar, dan ukuran beda memang wajar kalau isinya diubah.
                    // Karena itu hanya diwarnai, tidak dipakai memilih otomatis.
                    const delta = r.file.size - m.size;
                    const same  = delta === 0;
                    return (
                      <button key={k} onClick={() => applyRowMatch(i, m)} style={S.locateOption(r.path === m.path && r.type === m.type)}>
                        <span style={{ fontWeight: 700, marginRight: 6 }}>[{m.type}]</span>
                        <code style={{ fontSize: 11 }}>{m.path}</code>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {fmtBytes(m.size)}
                          {' · '}
                          <b style={{ color: same ? '#16a34a' : '#b45309' }}>
                            {same ? t('fdeploy.locSizeSame') : (delta > 0 ? '+' : '−') + fmtBytes(Math.abs(delta))}
                          </b>
                          {' · '}
                          {fmtIsoWib(new Date(m.mtime).toISOString(), false)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );})}
        </div>
      )}
      {locating && <div style={S.locateBox('#f8fafc', '#e2e8f0')}>{t('fdeploy.locSearching')}</div>}
      {rows.length > 0 && (
        <div style={S.locateBox('#f8fafc', '#e2e8f0')}>
          <b>{t('fdeploy.mdPlanTitle')}</b>
          <div style={{ marginTop: 4 }}>
            {['frontend', 'backend', 'ai-service']
              .filter((tp) => counts[tp])
              .map((tp) => `${tp}: ${counts[tp]}`)
              .join(' · ')}
          </div>
          <ol style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12 }}>
            <li>{t('fdeploy.mdStepWrite', { n: rows.length })}</li>
            {needBuild && <li>{t('fdeploy.mdStepBuild')}</li>}
            {pendingRestart.length > 0 && <li>{t('fdeploy.mdStepRestart', { list: pendingRestart.join(' → ') })}</li>}
          </ol>
          {needBuild && pendingRestart.length > 0 && (
            <div style={{ marginTop: 4, color: '#b45309' }}>{t('fdeploy.mdOrderNote')}</div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Btn variant="primary" disabled={!canDeploy} onClick={() => setConfirm(true)}>
          {needBuild && pendingRestart.length > 0 ? t('fdeploy.btnDeployMixed', { n: rows.length })
            : needBuild ? t('fdeploy.btnDeployRebuild', { n: rows.length })
            : t('fdeploy.btnDeployRestart', { n: rows.length })}
        </Btn>
        {rows.length > 0 && <Btn disabled={busy || building} onClick={() => setRows([])}>{t('fdeploy.bersihkanSemua')}</Btn>}
      </div>
      {(log || building) && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>{building ? t('fdeploy.buildBerjalan') : t('fdeploy.logBuild')}</div>
          <pre ref={logRef} style={{ margin: 0, maxHeight: 360, overflow: 'auto', fontSize: 12, lineHeight: 1.45, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log || ' '}</pre>
        </div>
      )}
      {confirm && (
        <Modal title={t('fdeploy.konfirmasiMultiDeploy')} onClose={() => setConfirm(false)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            <Tx text={t('fdeploy.confirmDeployMulti', { n: rows.length, type: ['frontend', 'backend', 'ai-service'].filter((tp) => counts[tp]).join(' + ') })} />
          </p>
          <ol style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, paddingLeft: 20, marginTop: 0 }}>
            <li>{t('fdeploy.mdStepWrite', { n: rows.length })}</li>
            {needBuild && <li>{t('fdeploy.mdStepBuild')}</li>}
            {pendingRestart.length > 0 && <li>{t('fdeploy.mdStepRestart', { list: pendingRestart.join(' → ') })}</li>}
          </ol>
          {needBuild && pendingRestart.length > 0 && (
            <p style={{ fontSize: 12, color: '#b45309', marginTop: 0 }}>{t('fdeploy.mdOrderNote')}</p>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirm(false)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="primary" onClick={doDeploy}>{t('fdeploy.yaDeploySemua')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 3: Operasi (build / restart / log) ──────────────────────────────────
function OpsPanel({ api, notify }) {
  const {
    t
  } = useLang();

  const [buildLog, setBuildLog]   = useState('');
  const [building, setBuilding]   = useState(false);
  const [restarting, setRestart]    = useState(false);
  const [restartingAi, setRestartAi] = useState(false);
  const [logText, setLogText]       = useState('');
  const [logLines, setLogLines]     = useState(100);
  const [loadingLog, setLoading]    = useState(false);
  const [copied, setCopied]         = useState(false);
  const [confirm, setConfirm]       = useState(null); // 'build' | 'restart' | 'restart-ai'
  const buildRef = useRef(null);
  const logRef   = useRef(null);

  useEffect(() => { if (buildRef.current) buildRef.current.scrollTop = buildRef.current.scrollHeight; }, [buildLog]);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logText]);

  // Stream `npm run build` via fetch ReadableStream.
  const runBuild = async () => {
    setConfirm(null); setBuilding(true); setBuildLog('▶ Memulai build…\n');
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/api/file-deploy/build`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        setBuildLog((prev) => prev + dec.decode(value, { stream: true }));
      }
      setBuildLog((prev) => {
        const m = /__BUILD_DONE__ exit=(\d+)/.exec(prev);
        if (m && m[1] === '0') notify(t('fdeploy.success'), t('fdeploy.buildSelesaiHardRefreshCtrl'));
        else if (m) notify(t('fdeploy.error'), `Build gagal (exit ${m[1]}). Cek log build.`);
        return prev;
      });
    } catch (e) {
      setBuildLog((prev) => prev + `\n__BUILD_ERROR__ ${e.message}\n`);
      notify(t('fdeploy.error'), 'Build gagal: ' + e.message);
    } finally { setBuilding(false); }
  };

  const restartBackend = async () => {
    setConfirm(null); setRestart(true);
    try {
      await api('/api/file-deploy/restart', { method: 'POST', body: '{}' });
      notify(t('fdeploy.warning'), t('fdeploy.backendSedangRestartTunggu10'));
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setRestart(false); }
  };

  // Memakai endpoint yang SAMA dengan restart backend, hanya beda `type`.
  // Sebelumnya tombol ini memanggil /api/file-explorer/restart-ai — duplikat
  // perintah pm2 yang terlewat saat migrasi root, sehingga masih menjalankan
  // `cd /ai-service` (gagal: No such file or directory) sementara deskripsi di
  // UI sudah menampilkan /awsbasis/ai-service. Jalur ini mengambil path dari
  // ROOTS (env-driven), DAN memberi dua hal yang tidak dimiliki endpoint lama:
  // pengawas deployWatchdog serta audit log FILE_DEPLOY_RESTART.
  const restartAiService = async () => {
    setConfirm(null); setRestartAi(true);
    try {
      await api('/api/file-deploy/restart', { method: 'POST', body: JSON.stringify({ type: 'ai-service' }) });
      notify(t('fdeploy.warning'), t('fdeploy.aiServiceSedangRestartTunggu'));
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setRestartAi(false); }
  };

  const loadLog = async () => {
    setLoading(true);
    try {
      const r = await api(`/api/file-deploy/logs?lines=${logLines}`);
      setLogText(r.log && r.log.trim() ? r.log : t('fdeploy.logKosong'));
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setLoading(false); }
  };

  const copyLog = async () => {
    try {
      await navigator.clipboard.writeText(logText);
      setCopied(true);
      notify(t('fdeploy.success'), t('fdeploy.logDisalinClipboard'));
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      notify(t('fdeploy.error'), 'Gagal menyalin log: ' + e.message);
    }
  };

  const card = { border: '1px solid #e2e8f0', borderRadius: 10, padding: '1rem 1.1rem', marginBottom: 14 };
  const cardTitle = { fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' };
  const cardDesc = { fontSize: 12, color: '#64748b', margin: '0 0 12px' };

  return (
    <div>
      {/* Build frontend */}
      <div style={card}>
        <h4 style={cardTitle}>{t('fdeploy.buildFrontend')}</h4>
        <p style={cardDesc}><Tx text={t('fdeploy.opsRebuildDesc')} code={t('fdeploy.rmRfNodeModulesCache')} /></p>
        <Btn variant="primary" disabled={building} onClick={() => setConfirm('build')}>{building ? t('fdeploy.building') : t('fdeploy.buildFrontendSekarang')}</Btn>
        {(buildLog || building) && (
          <pre ref={buildRef} style={{ margin: '12px 0 0', maxHeight: 320, overflow: 'auto', fontSize: 12, lineHeight: 1.45, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{buildLog || ' '}</pre>
        )}
      </div>
      {/* Restart backend */}
      <div style={card}>
        <h4 style={cardTitle}>{t('fdeploy.restartBackend')}</h4>
        <p style={cardDesc}><Tx text={t('fdeploy.opsRestartBackendDesc', { plain: t('fdeploy.pm2Restart') })} code={t('fdeploy.pm2DeleteDashboardPm2Start')} /></p>
        <Btn variant="warning" disabled={restarting} onClick={() => setConfirm('restart')}>{restarting ? t('fdeploy.restarting') : t('fdeploy.restartBackendSekarang')}</Btn>
      </div>
      {/* Restart AI-service */}
      <div style={card}>
        <h4 style={cardTitle}>{t('fdeploy.restartAiService')}</h4>
        <p style={cardDesc}><Tx text={t('fdeploy.opsRestartAiDesc')} code={t('fdeploy.pm2DeleteAiServicePm2')} /></p>
        <Btn variant="warning" disabled={restartingAi} onClick={() => setConfirm('restart-ai')}>{restartingAi ? t('fdeploy.restarting') : t('fdeploy.restartAiServiceSekarang')}</Btn>
      </div>
      {/* Log backend */}
      <div style={card}>
        <h4 style={cardTitle}>{t('fdeploy.logSemuaProses')}</h4>
        <p style={cardDesc}>{t('fdeploy.ambilLogTerbaruSemuaProses')} <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{t('fdeploy.pm2Logs')}</code>{t('fdeploy.dashboardAiServiceDll')}</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>{t('fdeploy.jumlahBaris')}</span>
          <Select value={logLines} onChange={(e) => setLogLines(Number(e.target.value))} style={{ width: 100 }}>
            {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
          <Btn variant="primary" size="sm" disabled={loadingLog} onClick={loadLog}>{loadingLog ? t('fdeploy.memuat') : t('fdeploy.tampilkanLog')}</Btn>
          {logText && (
            <Btn size="sm" disabled={loadingLog} onClick={copyLog}>{copied ? t('fdeploy.disalin') : t('fdeploy.copyLog')}</Btn>
          )}
        </div>
        {logText && (
          <pre ref={logRef} style={{ margin: 0, maxHeight: 420, overflow: 'auto', fontSize: 12, lineHeight: 1.45, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{logText}</pre>
        )}
      </div>
      {confirm && (
        <Modal title={confirm === 'build' ? t('fdeploy.konfirmasiBuild') : confirm === 'restart-ai' ? t('fdeploy.konfirmasiRestartAiService') : t('fdeploy.konfirmasiRestart')} onClose={() => setConfirm(null)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            {confirm === 'build'
              ? t('fdeploy.jalankanRebuildFrontendSekarangProses')
              : confirm === 'restart-ai'
              ? t('fdeploy.restartAiServiceSekarangKoneksi')
              : t('fdeploy.restartBackendSekarangSemuaKoneksi')}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirm(null)}>{t('fdeploy.batal')}</Btn>
            <Btn variant={confirm === 'build' ? t('fdeploy.primary') : t('fdeploy.warning')}
              onClick={confirm === 'build' ? runBuild : confirm === 'restart-ai' ? restartAiService : restartBackend}>
              {confirm === 'build' ? t('fdeploy.yaBuild') : confirm === 'restart-ai' ? t('fdeploy.yaRestartAiService') : t('fdeploy.yaRestart')}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: File Explorer ───────────────────────────────────────────────────────
// Browse root /, upload, download, delete, rename, view/edit, multi-select,
// drag & drop upload. Responsive (mobile-friendly).
function FileExplorerPanel({ api, notify }) {
  const {
    t
  } = useLang();

  const [curPath, setCurPath]           = useState('/');
  const [entries, setEntries]           = useState([]);
  const [searchQ, setSearchQ]           = useState(''); // filter nama di folder aktif
  const [sortBy, setSortBy]             = useState({ key: 'name', dir: 'asc' });
  const [limited, setLimited]           = useState(false);
  const [total, setTotal]               = useState(0);
  const [busy, setBusy]                 = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName]           = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading]       = useState(false);
  // ── New states ──────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 700);
  const [selected, setSelected]         = useState(new Set());
  const [dragging, setDragging]         = useState(false);
  const [favorites, setFavorites] = useState(() => { try { return JSON.parse(localStorage.getItem('fexp.favorites') || '[]'); } catch { return []; } });
  // File Explorer favorites are stored per account on the server (user_preferences key 'fexp_favorites');
  // localStorage is only a cache/fallback so favorites follow the user across devices.
  const saveFavorites = (list) => {
    setFavorites(list);
    try { localStorage.setItem('fexp.favorites', JSON.stringify(list)); } catch {}
    axiosApi.put('/api/user/preferences/fexp_favorites', { value: list }).catch(() => {});
  };
  useEffect(() => {
    let alive = true;
    axiosApi.get('/api/user/preferences/fexp_favorites').then((r) => {
      if (!alive) return;
      const server = Array.isArray(r?.data?.value) ? r.data.value : null;
      let local = [];
      try { local = JSON.parse(localStorage.getItem('fexp.favorites') || '[]'); } catch {}
      if (!Array.isArray(local)) local = [];
      if (server === null) {
        // First time on the server: migrate this device's local favorites.
        if (local.length) axiosApi.put('/api/user/preferences/fexp_favorites', { value: local }).catch(() => {});
        return;
      }
      setFavorites(server);
      try { localStorage.setItem('fexp.favorites', JSON.stringify(server)); } catch {}
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const isFav = favorites.includes(curPath);
  const toggleFav = () => { saveFavorites(isFav ? favorites.filter(p => p !== curPath) : [...favorites, curPath]); };
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null); // { toUpload:[File], collisions:[{file,existing}] } — nunggu konfirmasi timpa
  const [editing, setEditing]           = useState(null); // { path, name, content, originalContent, saving }
  const [editorBusy, setEditorBusy]     = useState(false);
  const [diffTarget, setDiffTarget]     = useState(null); // { type, rel } — file yg riwayat diff-nya sedang dilihat
  // ── Cari di dalam editor ────────────────────────────────────────────────────
  // `matches` = daftar indeks karakter, dihitung ulang tiap query/konten berubah.
  // Menyimpan INDEKS (bukan potongan teks) supaya setSelectionRange bisa
  // langsung dipakai tanpa mencari ulang saat berpindah antar hasil.
  const [findQ, setFindQ]       = useState('');
  const [findIdx, setFindIdx]   = useState(0);
  const [findCase, setFindCase] = useState(false);
  const taRef      = useRef(null);
  // ── Gutter nomor baris + posisi kursor ──────────────────────────────────────
  // Gutter adalah elemen TERPISAH yang digulirkan mengikuti textarea, bukan
  // background bergaris. Nomor baris harus tetap sejajar saat isinya digulir,
  // dan satu-satunya cara textarea polos bisa melakukannya adalah menyalin
  // scrollTop-nya ke elemen di sebelahnya.
  //
  // Konsekuensinya: gutter dan textarea WAJIB memakai fontFamily, fontSize,
  // lineHeight, dan paddingTop yang identik. Kalau salah satu berbeda,
  // penyimpangannya menumpuk ke bawah dan baris 300 akan meleset jauh —
  // kesalahan yang tidak terlihat di layar pertama dan justru muncul saat
  // nomor barisnya paling dibutuhkan. Nilainya diambil dari satu konstanta
  // di bawah supaya tidak bisa menyimpang diam-diam.
  const gutterRef  = useRef(null);
  const [caret, setCaret] = useState({ line: 1, col: 1, sel: 0 });
  const findRef    = useRef(null);
  const fileRef    = useRef(null);
  const dragCntRef = useRef(0); // counter untuk nested dragenter/leave

  // ── Responsive detection ────────────────────────────────────────────────────
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── Load direktori ───────────────────────────────────────────────────────────
  const load = useCallback(async (p) => {
    setBusy(true); setSelected(new Set());
    try {
      const r = await api(`/api/file-explorer/list?path=${encodeURIComponent(p)}`);
      setCurPath((prev) => { if (prev !== r.path) setSearchQ(''); return r.path; }); setEntries(r.entries || []);
      setLimited(!!r.limited); setTotal(r.total || 0);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  }, [api, notify]);

  useEffect(() => { load('/'); }, [load]);

  // Folder yang terpotong limit: cari juga ke server (param q) supaya file di
  // luar entri yang ter-load tetap ketemu. Hasil server digabung ke entries.
  const [limitedHits, setLimitedHits] = useState([]);
  useEffect(() => {
    const q = searchQ.trim();
    if (!limited || !q) { setLimitedHits([]); return; }
    let alive = true;
    const tm = setTimeout(async () => {
      try {
        const r = await api(`/api/file-explorer/list?path=${encodeURIComponent(curPath)}&q=${encodeURIComponent(q)}`);
        if (alive) setLimitedHits(r.entries || []);
      } catch (_) {}
    }, 300);
    return () => { alive = false; clearTimeout(tm); };
  }, [searchQ, limited, curPath, api]);

  // ── Upload (shared antara file input dan drag & drop) ────────────────────────
  // Backend sudah auto-backup file lama sebelum ditimpa (lihat fileExplorerRoutes.js),
  // tapi user tetap perlu tahu & mengonfirmasi SEBELUM upload jalan — supaya tidak
  // menimpa file yang salah tanpa sadar (lihat entries yg sudah ke-load di folder ini).
  const runUpload = async (files) => {
    setUploading(true); let ok = 0, overwritten = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f); fd.append('path', curPath);
        const r = await axiosApi.post('/api/file-explorer/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++; if (r.data?.existed) overwritten++;
      }
      notify(t('fdeploy.success'), overwritten
        ? t('fdeploy.fileTerUploadMenimpaFile', {
        ok,
        curPath,
        overwritten
      })
        : t('fdeploy.fileTerUpload', {
        ok,
        curPath
      }));
      load(curPath);
    } catch (e) { notify(t('fdeploy.error'), e.response?.data?.error || e.message); }
    finally { setUploading(false); }
  };

  const doUpload = (files) => {
    if (!files || !files.length) return;
    const existingNames = new Set(entries.filter(e => e.type === 'file').map(e => e.name));
    const collisions = files.filter(f => existingNames.has(f.name))
      .map(f => ({ file: f, existing: entries.find(e => e.name === f.name) }));
    if (collisions.length) { setPendingUpload({ toUpload: files, collisions }); return; }
    runUpload(files);
  };

  const confirmPendingUpload = () => {
    if (!pendingUpload) return;
    const files = pendingUpload.toUpload; setPendingUpload(null);
    runUpload(files);
  };

  const onFileInput = (e) => { doUpload(Array.from(e.target.files || [])); e.target.value = ''; };

  // ── Drag & drop ──────────────────────────────────────────────────────────────
  // Drag & drop upload dinonaktifkan - upload hanya lewat tombol Upload.
  // Handler disisakan sebagai no-op supaya file yang tidak sengaja di-drop
  // tidak membuat browser membuka file tersebut.
  const onDragEnter = (e) => { e.preventDefault(); };
  const onDragOver  = (e) => { e.preventDefault(); };
  const onDragLeave = (e) => { e.preventDefault(); };
  const onDrop      = (e) => { e.preventDefault(); };

  // ── Download ─────────────────────────────────────────────────────────────────
  const download = async (filePath, filename) => {
    try {
      const res = await axiosApi.get(`/api/file-explorer/download?path=${encodeURIComponent(filePath)}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
  };

  // ── Delete single ─────────────────────────────────────────────────────────────
  const doDelete = async () => {
    const p = deleteTarget; setDeleteTarget(null); setBusy(true);
    try {
      await api('/api/file-explorer/delete', { method: 'POST', body: JSON.stringify({ path: p }) });
      notify(t('fdeploy.success'), t('fdeploy.dihapus', {
        p
      })); load(curPath);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setBusy(false); }
  };

  // ── Delete batch ──────────────────────────────────────────────────────────────
  const doBatchDelete = async () => {
    setConfirmBatch(false);
    const paths = Array.from(selected);
    setBusy(true); let ok = 0, fail = 0;
    for (const p of paths) {
      try { await api('/api/file-explorer/delete', { method: 'POST', body: JSON.stringify({ path: p }) }); ok++; }
      catch (_) { fail++; }
    }
    setSelected(new Set());
    notify(fail ? t('fdeploy.error') : t('fdeploy.success'), `Dihapus ${ok} item${fail ? t('fdeploy.gagal2', {
      fail
    }) : ''}.`);
    load(curPath); setBusy(false);
  };

  // ── Download batch ─────────────────────────────────────────────────────────────
  const doBatchDownload = async () => {
    const filePaths = Array.from(selected).filter(p => {
      const nm = p.split('/').pop();
      const e  = entries.find(e => e.name === nm);
      return e && e.type === 'file';
    });
    for (const p of filePaths) {
      await download(p, p.split('/').pop());
      await new Promise(r => setTimeout(r, 400));
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────────────
  const doRename = async () => {
    if (!newName.trim()) return;
    const { path: oldPath } = renameTarget; setRenameTarget(null);
    try {
      await api('/api/file-explorer/rename', { method: 'POST', body: JSON.stringify({ path: oldPath, newName: newName.trim() }) });
      notify(t('fdeploy.success'), t('fdeploy.berhasilRename')); load(curPath);
    } catch (e) { notify(t('fdeploy.error'), e.message); }
  };

  // ── Multi-select helpers ───────────────────────────────────────────────────────
  const fp = (name) => curPath === '/' ? `/${name}` : t('fdeploy.teks2', {
    curPath,
    name
  });
  const toggleSelect = (path) => setSelected(prev => { const n = new Set(prev); n.has(path) ? n.delete(path) : n.add(path); return n; });
  const sq = searchQ.trim().toLowerCase();
  const shown = useMemo(() => {
    let list = sq ? entries.filter(e => e.name.toLowerCase().includes(sq)) : entries.slice();
    if (sq && limitedHits.length) {
      const have = new Set(list.map(e => e.name));
      list = list.concat(limitedHits.filter(e => !have.has(e.name)));
    }
    const m = sortBy.dir === 'asc' ? 1 : -1;
    const val = (e) => sortBy.key === 'size' ? (e.type === 'file' ? (e.size || 0) : -1)
      : sortBy.key === 'mtime' ? (e.mtime ? new Date(e.mtime).getTime() : 0) : e.name.toLowerCase();
    return list.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'dir' ? -1 : 1; // folder selalu di atas
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * m;
      if (va > vb) return 1 * m;
      return a.name.localeCompare(b.name);
    });
  }, [entries, sq, sortBy, limitedHits]);
  const toggleSort = (key) => setSortBy(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' });
  const sortArrow = (key) => sortBy.key === key ? (sortBy.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const sortHdr = { cursor: 'pointer', userSelect: 'none' };
  const allSelected  = shown.length > 0 && shown.every(e => selected.has(fp(e.name)));
  const toggleAll    = () => setSelected(allSelected ? new Set() : new Set(shown.map(e => fp(e.name))));

  // ── File viewer / editor ───────────────────────────────────────────────────────
  const TEXT_EXTS = new Set(['js','jsx','ts','tsx','json','css','html','htm','xml','yaml','yml','md','txt','sh','bash','py','rb','java','c','cpp','h','env','log','conf','cfg','ini','toml','sql','csv','svg','gitignore','htaccess','nginx','pem','key']);
  const isText = (name) => TEXT_EXTS.has((name.split('.').pop() || '').toLowerCase()); // eslint-disable-line

  const openEditor = async (filePath, filename) => {
    setEditorBusy(true);
    try {
      const r = await api(`/api/file-explorer/read?path=${encodeURIComponent(filePath)}`);
      setEditing({ path: filePath, name: filename, content: r.content, originalContent: r.content, saving: false });
      setFindQ(''); setFindIdx(0);   // jangan bawa query file sebelumnya
    } catch (e) { notify(t('fdeploy.error'), e.message); }
    finally { setEditorBusy(false); }
  };

  const saveEditor = async () => {
    if (!editing) return;
    setEditing(prev => ({ ...prev, saving: true }));
    try {
      await api('/api/file-explorer/write', { method: 'POST', body: JSON.stringify({ path: editing.path, content: editing.content }) });
      setEditing(prev => ({ ...prev, originalContent: prev.content, saving: false }));
      notify(t('fdeploy.success'), t('fdeploy.tersimpan', {
        name: editing.name
      }));
    } catch (e) { setEditing(prev => ({ ...prev, saving: false })); notify(t('fdeploy.error'), e.message); }
  };

  // ── Layout helpers ──────────────────────────────────────────────────────────────
  const segs      = curPath === '/' ? [] : curPath.split('/').filter(Boolean);
  const parentPath = curPath.split('/').slice(0, -1).join('/') || '/';
  const cols = isMobile ? '20px 1fr auto' : '20px 1fr 90px 145px auto';
  const rowSt = { display: 'grid', gridTemplateColumns: cols, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', gap: 4 };
  // Daftar posisi semua kecocokan. Dihitung dari editing.content supaya hasil
  // tetap benar setelah teks diedit — bukan di-cache saat pencarian dimulai.
  const findMatches = useMemo(() => {
    const hay = editing?.content || '';
    if (!findQ || !hay) return [];
    const h = findCase ? hay : hay.toLowerCase();
    const n = findCase ? findQ : findQ.toLowerCase();
    const out = [];
    let i = h.indexOf(n);
    // Batas 5000 hasil: query satu huruf pada file besar bisa menghasilkan
    // ratusan ribu entri dan membekukan tab tanpa gejala yang jelas.
    while (i !== -1 && out.length < 5000) { out.push(i); i = h.indexOf(n, i + Math.max(1, n.length)); }
    return out;
  }, [editing?.content, findQ, findCase]);

  // Posisi kursor dihitung dari selectionStart, bukan dari event papan ketik.
  // Kursor juga berpindah lewat klik, seret, Ctrl+Home, dan hasil pencarian —
  // menghitungnya dari tombol yang ditekan akan benar untuk sebagian kasus saja,
  // dan indikator yang kadang benar lebih menyesatkan daripada tidak ada.
  const syncCaret = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    const upto = ta.value.slice(0, ta.selectionStart);
    const nl   = upto.lastIndexOf('\n');
    setCaret({
      line: upto.split('\n').length,
      col:  ta.selectionStart - nl,
      sel:  Math.abs(ta.selectionEnd - ta.selectionStart),
    });
  }, []);

  // Menyorot hasil ke-`k`: seleksi teksnya, lalu gulirkan ke tengah.
  // Urutannya penting — setSelectionRange dulu, scrollTop belakangan di dalam
  // rAF, karena browser menggulir sendiri ke caret setelah focus() dan akan
  // menimpa nilai yang kita set lebih dulu.
  // JANGAN memanggil ta.focus() di sini. Efek auto-lompat berjalan pada SETIAP
  // ketukan di kotak cari; kalau fokus dipindah ke textarea, huruf kedua dan
  // seterusnya masuk ke ISI FILE — dan Backspace menghapus kode, bukan query.
  // Itu kehilangan data, bukan sekadar gangguan. Seleksi tetap diset (browser
  // menampilkannya redup saat textarea tidak fokus) dan gulirnya tetap jalan,
  // jadi hasil tetap terlihat tanpa merebut ketikan. Esc = pindah ke teks.
  const gotoMatch = useCallback((k) => {
    const ta = taRef.current;
    if (!ta || !findMatches.length) return;
    const n = ((k % findMatches.length) + findMatches.length) % findMatches.length;
    setFindIdx(n);
    const pos = findMatches[n];
    ta.setSelectionRange(pos, pos + findQ.length);
    const cs = window.getComputedStyle(ta);
    const lh = parseFloat(cs.lineHeight) || 19;
    const line = ta.value.slice(0, pos).split('\n').length - 1;
    ta.scrollTop = Math.max(0, (parseFloat(cs.paddingTop) || 0) + line * lh - ta.clientHeight / 2);
    // setSelectionRange tidak memicu event `select`, jadi indikator posisi harus
    // diperbarui manual — tanpa ini melompat antar hasil pencarian membiarkan
    // "Brs/Kol" menunjuk tempat kursor sebelumnya.
    syncCaret();
  }, [findMatches, findQ, syncCaret]);

  // Lompat ke hasil pertama begitu query berubah, supaya Enter pertama berarti
  // "berikutnya" dan bukan "yang pertama". Deps SENGAJA hanya [findQ, findCase]:
  // kalau findMatches ikut masuk, mengetik di textarea akan memicu efek ini dan
  // menggulirkan layar menjauh dari kursor pada tiap ketukan.
  useEffect(() => {
    if (findQ && findMatches.length) gotoMatch(0);
    // eslint-disable-next-line
  }, [findQ, findCase]);

  // Metrik yang WAJIB sama antara textarea dan gutter. Lihat catatan di gutterRef.
  const EDITOR_FONT = 'ui-monospace, Menlo, Consolas, monospace';
  const EDITOR_FS   = 12.5;
  const EDITOR_LH   = 1.55;
  const EDITOR_PADY = 12;

  const lineCount = useMemo(
    () => (editing?.content ? editing.content.split('\n').length : 1),
    [editing?.content]);

  // Gutter mengikuti gulir textarea. Dipasang lewat onScroll pada textarea,
  // bukan efek — tidak ada state yang perlu ikut berubah, dan menaruhnya di
  // state akan me-render ulang seluruh modal pada setiap piksel gulir.
  const syncGutter = useCallback(() => {
    if (gutterRef.current && taRef.current) {
      gutterRef.current.scrollTop = taRef.current.scrollTop;
    }
  }, []);

  const isDirty = editing && editing.content !== editing.originalContent;
  const btnIco = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 3px', fontSize: 14 };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginBottom: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
        <button onClick={() => load('/')} style={crumbBtn}>/</button>
        {segs.map((s, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <button onClick={() => load('/' + segs.slice(0, i + 1).join('/'))} style={crumbBtn}>{s}</button>
          </span>
        ))}
      </div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={onFileInput} />
        <Btn variant="primary" size="sm" disabled={uploading || busy} onClick={() => fileRef.current && fileRef.current.click()}>
          {uploading ? t('fdeploy.uploading') : t('fdeploy.upload')}
        </Btn>
        <Btn size="sm" disabled={busy} onClick={() => load(curPath)}>{t('fdeploy.refresh')}</Btn>
        {curPath !== '/' && <Btn size="sm" disabled={busy} onClick={() => load(parentPath)}>{t('fdeploy.naik')}</Btn>}
            <Btn size="sm" onClick={toggleFav} title={isFav ? 'Remove this folder from favorites' : 'Add this folder to favorites'}>{isFav ? 'Unfavorite' : 'Favorite'}</Btn>
        {limited && <span style={{ fontSize: 11, color: '#d97706' }}>⚠️ {entries.length}/{total} {t('fdeploy.entri')}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearchQ(''); }}
            placeholder="Search in this folder..."
            spellCheck={false}
            style={{ width: isMobile ? 160 : 220, fontSize: 12, padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none' }}
          />
          {sq && <span style={{ fontSize: 11, color: shown.length ? '#64748b' : '#dc2626', whiteSpace: 'nowrap' }}>{shown.length}/{limited ? total : entries.length} match</span>}
        </div>
      </div>
          {favorites.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Favorites</span>
              {favorites.map((p) => (
                <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: p === curPath ? 'rgb(var(--brand-100))' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 4px 2px 10px', fontSize: 12 }}>
                  <button onClick={() => load(p)} title={p} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, fontFamily: 'ui-monospace, monospace', color: 'rgb(var(--brand-700))', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p === '/' ? '/' : p.split('/').filter(Boolean).slice(-1)[0]}</button>
                  <button onClick={() => saveFavorites(favorites.filter((x) => x !== p))} title="Remove from favorites" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#94a3b8', fontSize: 14, lineHeight: 1 }}>x</button>
                </span>
              ))}
            </div>
          )}
      {/* Batch action bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'rgb(var(--brand-700))', fontWeight: 600 }}>{selected.size} {t('fdeploy.itemDipilih')}</span>
          <Btn size="sm" onClick={doBatchDownload}>{t('fdeploy.download')}</Btn>
          <Btn size="sm" variant="danger" onClick={() => setConfirmBatch(true)}>{t('fdeploy.hapus')}</Btn>
          <Btn size="sm" onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto' }}>{t('fdeploy.batal2')}</Btn>
        </div>
      )}
      {/* Drop zone + file list */}
      <div
        onDragEnter={onDragEnter} onDragOver={onDragOver}
        onDragLeave={onDragLeave} onDrop={onDrop}
        style={{ border: `2px ${dragging ? 'dashed rgb(var(--brand-500))' : 'solid #e2e8f0'}`, borderRadius: 8, overflow: 'hidden', background: dragging ? 'rgb(var(--brand-50))' : undefined, transition: 'border-color 0.15s, background 0.15s' }}
      >
        {dragging ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', fontSize: 14, color: 'rgb(var(--brand-600))', fontWeight: 600 }}>
            <Tx text={t('fdeploy.dropHere')} code={curPath} codeStyle={{ background: 'rgb(var(--brand-100))', padding: '2px 6px', borderRadius: 4 }} />
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ ...rowSt, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={entries.length === 0} style={{ cursor: 'pointer' }} />
              <span style={sortHdr} onClick={() => toggleSort('name')} title="Sort by name">{t('fdeploy.nama')}{sortArrow('name')}</span>
              {!isMobile && <span style={{ ...sortHdr, textAlign: 'right' }} onClick={() => toggleSort('size')} title="Sort by size">{t('fdeploy.ukuran')}{sortArrow('size')}</span>}
              {!isMobile && <span style={{ ...sortHdr, textAlign: 'right' }} onClick={() => toggleSort('mtime')} title="Sort by last modified">{t('fdeploy.terakhirDiubah')}{sortArrow('mtime')}</span>}
              <span style={{ textAlign: 'right' }}>{t('fdeploy.aksi')}</span>
            </div>

            {busy ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('fdeploy.memuat')}</div>
            ) : entries.length === 0 ? (
              <Empty icon="📭" text={t('fdeploy.direktoriKosong')} />
            ) : (
              <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                {curPath !== '/' && (
                  <div style={{ ...rowSt, cursor: 'pointer' }} onClick={() => load(parentPath)}>
                    <span />
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}><span>📁</span>..</span>
                    {!isMobile && <span />}{!isMobile && <span />}<span />
                  </div>
                )}
                {sq && shown.length === 0 && (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No files or folders match "{searchQ.trim()}"</div>
                )}
                {shown.map((e) => {
                  const full = fp(e.name);
                  const chk  = selected.has(full);
                  return (
                    <div key={e.name} style={{ ...rowSt, background: chk ? '#f0f9ff' : undefined }}>
                      {/* Checkbox */}
                      <input type="checkbox" checked={chk} onChange={() => toggleSelect(full)} style={{ cursor: 'pointer' }} onClick={(ev) => ev.stopPropagation()} />
                      {/* Nama */}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, minWidth: 0 }}>
                        {e.type === 'dir' ? (
                          <button onClick={() => load(full)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit', fontSize: 13, color: 'rgb(var(--brand-600))', fontWeight: 500, padding: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                            <span style={{ flexShrink: 0 }}>📁</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                          </button>
                        ) : (
                          <button onClick={() => openEditor(full, e.name)} disabled={editorBusy} title={t('fdeploy.viewEdit')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit', fontSize: 13, color: '#1e293b', padding: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', textAlign: 'left' }}>
                            <span style={{ flexShrink: 0 }}>📄</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                          </button>
                        )}
                      </span>
                      {/* Ukuran — sembunyikan di mobile */}
                      {!isMobile && <span style={{ textAlign: 'right', fontSize: 12, color: '#64748b' }}>{e.type === 'file' ? fmtBytes(e.size) : '—'}</span>}
                      {/* Waktu — sembunyikan di mobile */}
                      {!isMobile && <span style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8' }}>{e.mtime ? new Date(e.mtime).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>}
                      {/* Aksi */}
                      <span style={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
                        {e.type === 'file' && (
                          <button onClick={() => openEditor(full, e.name)} title={t('fdeploy.viewEdit')} disabled={editorBusy} style={btnIco}>📝</button>
                        )}
                        {e.type === 'file' && (
                          <button onClick={() => download(full, e.name)} title={t('fdeploy.download2')} style={btnIco}>⬇️</button>
                        )}
                        {e.type === 'file' && /\.(js|jsx)$/i.test(e.name) && absToTypeRel(full) && (
                          <button onClick={() => setDiffTarget(absToTypeRel(full))} title={t('fdeploy.riwayatDiffTracingDeployTab')} style={btnIco}>🕓</button>
                        )}
                        <button onClick={() => { setRenameTarget({ path: full, name: e.name }); setNewName(e.name); }} title={t('fdeploy.rename')} style={btnIco}>✏️</button>
                        <button onClick={() => setDeleteTarget(full)} title={t('fdeploy.hapus2')} style={btnIco}>🗑️</button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      {/* ── Modal: File Editor/Viewer ──────────────────────────────────────────── */}
      {editing && (
        <Modal title={`${isDirty ? '● ' : ''}${editing.name}`} onClose={() => { if (isDirty && !window.confirm(t('fdeploy.adaPerubahanBelumDisimpanTutup'))) return; setEditing(null); }} maxWidth={860}>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginRight: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{editing.path}</span>
            {/* ── Cari (Ctrl+F) ──────────────────────────────────────────────
                Textarea polos tidak bisa menyorot banyak kecocokan sekaligus,
                jadi navigasinya lewat seleksi: satu hasil aktif pada satu waktu.
                Enter = berikutnya, Shift+Enter = sebelumnya, Esc = kosongkan. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                ref={findRef}
                value={findQ}
                onChange={(e) => { setFindQ(e.target.value); setFindIdx(0); }}
                placeholder={t('fdeploy.cariDiFile')}
                spellCheck={false}
                style={{ width: 170, fontSize: 12, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')  { e.preventDefault(); gotoMatch(e.shiftKey ? findIdx - 1 : findIdx + 1); }
                  // Esc pertama: pindah ke teks pada posisi hasil aktif (query
                  // dipertahankan supaya pencacahnya tidak hilang). Mengosongkan
                  // query cukup lewat tombol ✕ bawaan input atau Ctrl+A.
                  if (e.key === 'Escape') { e.preventDefault(); taRef.current?.focus(); }
                }}
              />
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => setFindCase(v => !v)} title={t('fdeploy.bedakanHurufBesarKecil')}
                style={{ ...btnIco, fontSize: 11, fontWeight: 700, color: findCase ? 'rgb(var(--brand-600))' : '#94a3b8' }}>Aa</button>
              <span style={{ fontSize: 11, color: findQ && !findMatches.length ? '#dc2626' : '#64748b', minWidth: 54, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {!findQ ? '' : findMatches.length ? `${findIdx + 1}/${findMatches.length}` : t('fdeploy.nihil')}
              </span>
              {/* onMouseDown+preventDefault: tanpa ini tombol mengambil fokus dari
                  kotak cari, sehingga Enter berikutnya tidak lagi berpindah hasil. */}
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => gotoMatch(findIdx - 1)} disabled={!findMatches.length} title={t('fdeploy.hasilSebelumnya')} style={btnIco}>▲</button>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => gotoMatch(findIdx + 1)} disabled={!findMatches.length} title={t('fdeploy.hasilBerikutnya')} style={btnIco}>▼</button>
            </div>
            <Btn size="sm" onClick={() => setEditing(prev => ({ ...prev, content: prev.originalContent }))}>{t('fdeploy.reset')}</Btn>
            {isDirty && (
              <Btn variant="primary" size="sm" disabled={editing.saving} onClick={saveEditor}>
                {editing.saving ? t('fdeploy.menyimpan') : t('fdeploy.simpan')}
              </Btn>
            )}
          </div>
          <div style={{ display: 'flex', background: '#0f172a', borderRadius: 8, overflow: 'hidden' }}>
            {/* Gutter. aria-hidden: nomor baris adalah bantuan visual; pembaca
                layar sudah membacakan isi textarea, dan menyisipkan deret angka
                di depannya hanya menambah kebisingan. */}
            <div
              ref={gutterRef}
              aria-hidden="true"
              title={t('fdeploy.nomorBaris')}
              style={{
                // Gutter tidak boleh ikut menentukan tinggi kontainer: isinya
                // diposisikan absolute sehingga tingginya mengikuti textarea.
                position: 'relative', alignSelf: 'stretch',
                flexShrink: 0, overflow: 'hidden', userSelect: 'none',
                fontFamily: EDITOR_FONT, fontSize: EDITOR_FS, lineHeight: EDITOR_LH,
                textAlign: 'right', color: '#475569', background: '#0b1220',
                borderRight: '1px solid #1e293b',
                width: `calc(${Math.max(2, String(lineCount).length)}ch + 20px)`,
                fontVariantNumeric: 'tabular-nums',
              }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: `${EDITOR_PADY}px 8px ${EDITOR_PADY}px 12px` }}>
                {Array.from({ length: lineCount }, (_, i) => (
                  <div key={i} style={{ color: i + 1 === caret.line ? '#e2e8f0' : undefined }}>{i + 1}</div>
                ))}
              </div>
            </div>
          <textarea
            ref={taRef}
            value={editing.content}
            onChange={(e) => setEditing(prev => ({ ...prev, content: e.target.value }))}
            onScroll={syncGutter}
            onSelect={syncCaret}
            onClick={syncCaret}
            onKeyUp={syncCaret}
            spellCheck={false}
            wrap="off"
            style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', height: 460, resize: 'vertical', whiteSpace: 'pre', overflowX: 'auto', fontFamily: EDITOR_FONT, fontSize: EDITOR_FS, lineHeight: EDITOR_LH, background: '#0f172a', color: '#e2e8f0', border: 'none', borderRadius: 0, padding: `${EDITOR_PADY}px 14px`, outline: 'none', tabSize: 2 }}
            onKeyDown={(ev) => {
              if (ev.key === 'Tab') {
                ev.preventDefault();
                const t = ev.target, s = t.selectionStart, v = t.value;
                t.value = v.slice(0, s) + '  ' + v.slice(t.selectionEnd);
                t.selectionStart = t.selectionEnd = s + 2;
                setEditing(prev => ({ ...prev, content: t.value }));
              }
              if (ev.ctrlKey && ev.key === 's') { ev.preventDefault(); saveEditor(); }
              // Ctrl+F direbut dari pencarian bawaan browser: pencarian browser
              // tidak bisa menemukan teks yang belum tergulir di dalam textarea.
              if (ev.ctrlKey && (ev.key === 'f' || ev.key === 'F')) { ev.preventDefault(); findRef.current?.focus(); findRef.current?.select(); }
            }}
          />
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
            fontSize: 11, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontFamily: EDITOR_FONT }}>
              {t('fdeploy.posisiKursor', { line: caret.line, col: caret.col })}
            </span>
            <span style={{ color: '#cbd5e1' }}>·</span>
            <span>{t('fdeploy.jumlahBaris', { total: lineCount })}</span>
            {caret.sel > 0 ? (
              <>
                <span style={{ color: '#cbd5e1' }}>·</span>
                <span style={{ color: 'rgb(var(--brand-600))' }}>{t('fdeploy.terpilihKarakter', { n: caret.sel })}</span>
              </>
            ) : null}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
            <kbd style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>{t('fdeploy.ctrlS')}</kbd> {t('fdeploy.simpan2')}
            <kbd style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>{t('fdeploy.tab')}</kbd>{t('fdeploy.indent2Spasi')}
            <kbd style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 10, marginLeft: 6 }}>Ctrl+F</kbd> {t('fdeploy.cari2')}
            <span style={{ marginLeft: 6 }}>· {t('fdeploy.escKeTeks')}</span>
          </div>
        </Modal>
      )}
      {/* ── Modal: Rename ─────────────────────────────────────────────────────── */}
      {renameTarget && (
        <Modal title={t('fdeploy.rename2', {
          name: renameTarget.name
        })} onClose={() => setRenameTarget(null)}>
          <Field label={t('fdeploy.namaBaru')}>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doRename()} autoFocus />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn onClick={() => setRenameTarget(null)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="primary" disabled={!newName.trim() || newName === renameTarget.name} onClick={doRename}>{t('fdeploy.simpan3')}</Btn>
          </div>
        </Modal>
      )}
      {/* ── Modal: Delete single ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <Modal title={t('fdeploy.konfirmasiHapus')} onClose={() => setDeleteTarget(null)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            <Tx text={t('fdeploy.confirmDeleteOne')} code={deleteTarget} codeStyle={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, wordBreak: 'break-all', fontSize: 12 }} />
                                    <br />{t('fdeploy.tindakan')} <b>{t('fdeploy.bisaDibatalkan')}</b>.
                                  </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setDeleteTarget(null)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="danger" onClick={doDelete}>{t('fdeploy.yaHapus')}</Btn>
          </div>
        </Modal>
      )}
      {/* ── Modal: Delete batch ───────────────────────────────────────────────── */}
      {confirmBatch && (
        <Modal title={t('fdeploy.konfirmasiHapusMassal')} onClose={() => setConfirmBatch(false)}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            <Tx text={t('fdeploy.confirmDeleteSelected')} b={`${selected.size}`} />
            <br />{t('fdeploy.tindakan')} <b>{t('fdeploy.bisaDibatalkan')}</b>.
                                  </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setConfirmBatch(false)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="danger" onClick={doBatchDelete}>{t('fdeploy.yaHapusSemua')}</Btn>
          </div>
        </Modal>
      )}
      {/* ── Modal: Konfirmasi timpa file (upload) ─────────────────────────────── */}
      {pendingUpload && (
        <Modal title={t('fdeploy.konfirmasiTimpaFile')} onClose={() => setPendingUpload(null)} maxWidth={560}>
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginTop: 0 }}>
            {t('fdeploy.konfirmasiTimpaPesan', {
              n: pendingUpload.collisions.length,
              total: pendingUpload.toUpload.length
            })}
                      </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxHeight: 260, overflowY: 'auto' }}>
            {pendingUpload.collisions.map(({ file, existing }) => (
              <div key={file.name} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#991b1b', wordBreak: 'break-all' }}>{file.name}</span>
                <span style={{ fontSize: 11.5, color: '#7f1d1d' }}>
                  {t('fdeploy.lama')} {fmtBytes(existing.size)}· {fmtTs(existing.mtime)}  {t('fdeploy.baru2')} {fmtBytes(file.size)}
                </span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.5, marginTop: 0 }}>
            <Tx text={t('fdeploy.explorerBackupNote')} code=".explorer-backups" />
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setPendingUpload(null)}>{t('fdeploy.batal')}</Btn>
            <Btn variant="danger" onClick={confirmPendingUpload}>{t('fdeploy.yaTimpa')} {pendingUpload.collisions.length} {t('fdeploy.file')}</Btn>
          </div>
        </Modal>
      )}
      {/* ── Modal: Riwayat Diff (shortcut dari File Explorer) ─────────────────── */}
      {diffTarget && (
        <FileDiffHistoryModal api={api} type={diffTarget.type} rel={diffTarget.rel} onClose={() => setDiffTarget(null)} />
      )}
    </div>
  );
}


// ── Tab: OS Console ──────────────────────────────────────────────────────────
// Terminal interaktif via WebSocket → SSH ke localhost.
export function OsConsolePanel({ target = 'local', targetLabel = null } = {}) {
  const {
    t
  } = useLang();

  const [connected, setConnected]   = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [output, setOutput]         = useState('');
  const [inputVal, setInputVal]     = useState('');
  // Riwayat perintah lokal (↑/↓ saat kotak input kosong atau sedang menelusuri).
  // Terpisah dari riwayat shell: shell punya miliknya sendiri, tapi panah di
  // sini tidak bisa diteruskan ke PTY tanpa merusak pengetikan multi-baris.
  const [history, setHistory]       = useState([]);
  const [histIdx, setHistIdx]       = useState(-1);   // -1 = tidak sedang menelusuri
  // Blok banyak baris menunggu konfirmasi sebelum dieksekusi.
  const [pending, setPending]       = useState(null); // null | string[]
  const CONFIRM_MIN_LINES = 3;
  const wsRef    = useRef(null);
  const outRef   = useRef(null);
  const inputRef = useRef(null);
  // ── Terminal sungguhan (DESKTOP saja) ────────────────────────────────────
  // Keputusan (a): xterm.js untuk desktop, kotak input lama tetap untuk mobile.
  // Alasannya bukan kemalasan — mengetuk kanvas xterm di HP sering tidak
  // memunculkan keyboard, dan tidak ada kotak untuk menempel. `top` adalah
  // kebutuhan desktop; menukar kegunaan di HP demi itu bukan pertukaran baik.
  const isMobile = useIsMobile();
  const termRef  = useRef(null);   // instance XTerm, null di mobile
  const fitRef   = useRef(null);
  const hostRef  = useRef(null);   // elemen tempat xterm dipasang

  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [output]);

  // Cleanup WS saat unmount
  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);

  // Bersihkan ANSI escape codes & normalkan CR
  const processOutput = (raw) => {
    return raw
      .replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '')           // CSI (warna, kursor)
      .replace(/\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g, '') // OSC (title, dll)
      .replace(/\x1b[^[\]()]/g, '')                       // ESC lainnya
      .replace(/\r\n/g, '\n')                             // CRLF → LF
      .replace(/\r(?!\n)/g, '\n');                        // lone CR → LF
  };

  const connect = () => {
    if (wsRef.current) return;
    setConnecting(true);
    setOutput('');
    if (termRef.current) termRef.current.clear();
    const token = localStorage.getItem('auth_token');
    // Web: pakai host halaman. APK/native: REACT_APP_API_URL absolut → ambil
    // host-nya, dan pilih wss bila https. (window.location.host di APK = localhost.)
    const apiBase = process.env.REACT_APP_API_URL || '';
    let wsUrl;
    if (apiBase) {
      const u = new URL(apiBase);
      const proto = u.protocol === 'https:' ? t('fdeploy.wss') : 'ws';
      wsUrl = `${proto}://${u.host}/ws/ssh-console?token=${encodeURIComponent(token)}&target=${encodeURIComponent(target)}`;
    } else {
      const proto = window.location.protocol === 'https:' ? t('fdeploy.wss') : 'ws';
      wsUrl = `${proto}://${window.location.host}/ws/ssh-console?token=${encodeURIComponent(token)}&target=${encodeURIComponent(target)}`;
    }
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen    = () => {
      setConnecting(false); setConnected(true);
      if (termRef.current) {
        termRef.current.focus();
        // Ukuran sebenarnya baru diketahui setelah terpasang. Dikirim di sini
        // supaya PTY tidak terlanjur menggambar untuk 200 kolom bawaan.
        const d = fitRef.current?.proposeDimensions?.();
        if (d?.cols && d?.rows) sendCtl({ type: 'resize', cols: d.cols, rows: d.rows });
      } else if (inputRef.current) inputRef.current.focus();
    };
    ws.onmessage = (e) => {
      const raw = e.data instanceof ArrayBuffer ? new TextDecoder().decode(e.data) : e.data;
      // Desktop: diserahkan MENTAH ke xterm. Justru escape ANSI-nya yang
      // membuat `top`/`htop`/`vi` bisa menggambar — `processOutput` yang
      // menghapusnya adalah sebab program layar penuh menumpuk jadi belasan
      // salinan layar. Jangan pernah menyaringnya di jalur ini.
      if (termRef.current) { termRef.current.write(raw); return; }
      // Mobile: perilaku lama, ANSI dibersihkan lalu ditambahkan ke buffer.
      setOutput(prev => {
        const next = prev + processOutput(raw);
        return next.length > 200000 ? next.slice(-150000) : next; // cap ~150 KB
      });
    };
    ws.onclose = () => {
      setConnected(false); setConnecting(false); wsRef.current = null;
      if (termRef.current) termRef.current.write('\r\n\x1b[33m[Koneksi ditutup]\x1b[0m\r\n');
      else setOutput(prev => prev + '\n[Koneksi ditutup]\n');
    };
    ws.onerror = () => {
      if (termRef.current) termRef.current.write('\r\n\x1b[31m[WebSocket error — cek log backend]\x1b[0m\r\n');
      else setOutput(prev => prev + '\n[WebSocket error — cek log backend]\n');
    };
    wsRef.current = ws;
  };

  const disconnect = () => { if (wsRef.current) wsRef.current.close(); };

  // Ketikan dikirim sebagai BINER, pesan kendali sebagai TEKS JSON. Backend
  // membedakannya lewat flag `isBinary` — bukan dengan menebak dari isinya,
  // yang akan salah pada hari user mengetik `echo '{"type":"resize"}'`.
  //
  // Aman dipasang lebih dulu daripada backend barunya: backend LAMA memanggil
  // `data.toString('utf8')` pada pesan biner, jadi ketikan tetap sampai.
  const sendRaw = (data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? new TextEncoder().encode(data) : data);
    }
  };
  const sendCtl = (obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));   // string → frame TEKS
    }
  };

  // Toolbar tombol khusus mobile: keyboard HP tak punya Esc/Tab/Ctrl/panah.
  // Kirim escape sequence langsung ke PTY (independen dari buffer input lokal).
  const sendKey = (seq) => {
    if (!connected) return;
    if (seq === '__clear') { setOutput(''); return; }
    if (seq.startsWith('__ins:')) {           // sisipkan char ke kotak input
      const ch = seq.slice(6);
      setInputVal((v) => v + ch);
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    sendRaw(seq);
    if (inputRef.current) inputRef.current.focus();
  };
  const MOBILE_KEYS = [
    { label: 'Ctrl-C', seq: '\x03' },
    { label: 'Ctrl-D', seq: '\x04' },
    { label: 'Esc',    seq: '\x1b' },
    { label: 'Tab',    seq: '\t' },
    { label: '↑',      seq: '\x1b[A' },
    { label: '↓',      seq: '\x1b[B' },
    { label: '←',      seq: '\x1b[D' },
    { label: '→',      seq: '\x1b[C' },
    { label: 'Clear',  seq: '__clear' },
    { label: '|',      seq: '__ins:|' },
    { label: '~',      seq: '__ins:~' },
    { label: '/',      seq: '__ins:/' },
    { label: '-',      seq: '__ins:-' },
  ];

  // Kirim isi kotak input. Baris kosong di ujung dibuang, tapi baris kosong di
  // TENGAH dipertahankan — di dalam heredoc (`cat <<EOF`) baris kosong adalah
  // isi yang sah, dan membuangnya diam-diam akan mengubah berkas yang ditulis.
  const linesOf = (s) => String(s).replace(/\r\n/g, '\n').split('\n').map(l => l.replace(/\s+$/, ''));
  const trimTrailingEmpty = (arr) => { const a = arr.slice(); while (a.length && a[a.length - 1] === '') a.pop(); return a; };

  // Fungsi biasa, BUKAN useCallback. Keduanya hanya membaca ref (`wsRef`,
  // `inputRef`) dan state lewat setter fungsional, jadi tidak ada manfaat
  // memoisasi — sementara daftar dependensinya akan menuntut direktif
  // `eslint-disable react-hooks/*` yang MENGGAGALKAN BUILD di proyek ini
  // (config ESLint-nya tidak memuat plugin itu).
  const execLines = (lines) => {
    // Tiap baris diakhiri '\r' — itu yang dilihat PTY sebagai Enter. Dikirim
    // sebagai SATU pesan: memecahnya per baris dengan jeda hanya menambah
    // peluang urutannya tertukar tanpa manfaat apa pun.
    sendRaw(lines.join('\r') + '\r');
    setInputVal('');
    setHistIdx(-1);
    const joined = lines.join('\n');
    setHistory(prev => (prev[prev.length - 1] === joined ? prev : [...prev, joined]).slice(-100));
    if (inputRef.current) inputRef.current.focus();
  };

  const submit = () => {
    const lines = trimTrailingEmpty(linesOf(inputVal));
    if (!lines.length) { sendRaw('\r'); return; }   // Enter kosong = Enter biasa ke shell
    // Lebih dari CONFIRM_MIN_LINES baris berarti banyak perintah sekaligus.
    // Satu tempelan yang salah di shell produksi tidak bisa ditarik kembali,
    // jadi ditahan dulu dan ditampilkan apa adanya untuk dibaca.
    if (lines.length > CONFIRM_MIN_LINES) { setPending(lines); return; }
    execLines(lines);
  };

  // ── Siklus hidup xterm (desktop) ─────────────────────────────────────────
  useEffect(() => {
    if (isMobile) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;

    const term = new XTerm({
      fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
      fontSize: 12.5,
      cursorBlink: true,
      scrollback: 5000,
      theme: { background: '#0f172a', foreground: '#e2e8f0', cursor: '#22c55e' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    termRef.current = term;
    fitRef.current = fit;

    // Ketikan → PTY. Tidak ada buffer lokal: xterm menyerahkan tiap tombol apa
    // adanya, jadi riwayat ↑/↓ kini datang dari SHELL sendiri — lebih benar
    // daripada riwayat lokal yang dulu dipakai kotak input.
    const dataSub = term.onData((d) => sendRaw(d));
    // Ukuran berubah → beritahu server, kalau tidak `top` menggambar untuk
    // ukuran yang salah dan tampilannya membungkus kacau.
    const sizeSub = term.onResize(({ cols, rows }) => sendCtl({ type: 'resize', cols, rows }));

    // `fit()` membaca ukuran elemen. Saat tab disembunyikan (`display:none`)
    // ukurannya NOL, dan memanggil fit() di keadaan itu akan menetapkan
    // terminal ke ukuran mustahil lalu mengirimkannya ke PTY. ResizeObserver
    // memecahkan keduanya sekaligus: ia diam saat tersembunyi, dan menyala
    // lagi persis ketika tab dibuka kembali — yang juga saat ukuran perlu
    // dihitung ulang.
    const safeFit = () => {
      if (!host.offsetWidth || !host.offsetHeight) return;
      try { fit.fit(); } catch (_) { /* ukuran belum stabil, tick berikutnya */ }
    };
    const ro = new ResizeObserver(safeFit);
    ro.observe(host);
    safeFit();

    // Tempelan banyak baris ditahan untuk konfirmasi. Ditangkap di fase
    // CAPTURE: xterm memasang penangan paste-nya sendiri di textarea
    // tersembunyi, dan tanpa capture ia sudah mengirimkannya ke PTY sebelum
    // kita sempat melihatnya.
    const onPaste = (e) => {
      const txt = (e.clipboardData || window.clipboardData)?.getData('text') || '';
      const lines = trimTrailingEmpty(linesOf(txt));
      if (lines.length > CONFIRM_MIN_LINES) {
        e.preventDefault();
        e.stopPropagation();
        setPending(lines);
      }
    };
    host.addEventListener('paste', onPaste, true);

    return () => {
      host.removeEventListener('paste', onPaste, true);
      ro.disconnect();
      dataSub.dispose();
      sizeSub.dispose();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
    // Sengaja hanya bergantung pada isMobile: terminal dibuat sekali dan hidup
    // selama panel terpasang. Panel ini tidak pernah dilepas saat pindah tab
    // (KEEP_ALIVE di FileDeployPage), jadi sesi SSH-nya ikut bertahan.
  }, [isMobile]);

  // Penanganan tombol untuk KOTAK INPUT MOBILE. Di desktop tidak terpakai —
  // xterm menyerahkan tiap tombol langsung ke PTY lewat onData.
  const onKeyDown = (e) => {
    if (!connected) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      // Shift+Enter menambah baris (perilaku bawaan textarea, dibiarkan lewat).
      e.preventDefault();
      submit();
      return;
    }
    if (e.ctrlKey && e.key === 'c') { sendRaw('\x03'); e.preventDefault(); return; }
    if (e.ctrlKey && e.key === 'd') { sendRaw('\x04'); e.preventDefault(); return; }
    if (e.ctrlKey && e.key === 'l') { setOutput(''); e.preventDefault(); return; }
    if (e.key === 'Tab')            { sendRaw('\t'); e.preventDefault(); return; }
    // Riwayat: hanya saat isi SATU baris (atau kosong). Pada isi multi-baris
    // panah harus tetap menggerakkan kursor, kalau tidak menyunting blok
    // tempelan jadi mustahil.
    const single = !inputVal.includes('\n');
    if (e.key === 'ArrowUp' && single && history.length) {
      e.preventDefault();
      const i = histIdx < 0 ? history.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(i); setInputVal(history[i]);
      return;
    }
    if (e.key === 'ArrowDown' && single && histIdx >= 0) {
      e.preventDefault();
      const i = histIdx + 1;
      if (i >= history.length) { setHistIdx(-1); setInputVal(''); }
      else { setHistIdx(i); setInputVal(history[i]); }
    }
  };

  const kbdSt = { background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 10 };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        {!connected ? (
          <Btn variant="primary" disabled={connecting} onClick={connect}>
            {connecting ? t('fdeploy.connecting') : t('fdeploy.connectSsh')}
          </Btn>
        ) : (
          <Btn variant="danger" onClick={disconnect}>{t('fdeploy.disconnect')}</Btn>
        )}
        <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: connected ? '#22c55e' : '#94a3b8', boxShadow: connected ? '0 0 0 2px #dcfce7' : 'none' }} />
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {connecting ? t('fdeploy.menghubungkan') : connected ? (targetLabel ? targetLabel + (String(target).startsWith('ssm:') ? '' : ' (SSH)') : t('fdeploy.ec2UserLocalhostSsh')) : t('fdeploy.terhubung')}
        </span>
        {(output || !isMobile) && (
          <Btn size="sm" onClick={() => { if (termRef.current) termRef.current.clear(); else setOutput(''); }}
            style={{ marginLeft: 'auto' }}>{t('fdeploy.clear')}</Btn>
        )}
      </div>
      {/* Output — DUA jalur yang saling eksklusif.
          Desktop: kanvas xterm. Elemen host WAJIB selalu ada di DOM (bukan
          dirender bersyarat pada `connected`), karena efek siklus hidup
          memasang terminal ke sini saat panel pertama dipasang. */}
      {!isMobile ? (
        <div style={{ background: '#0f172a', borderRadius: '8px 8px 0 0', padding: '8px 10px' }}>
          <div ref={hostRef} style={{ height: 460 }} />
          {!connected && !connecting && (
            <div style={{ color: '#475569', fontSize: 12, padding: '4px 4px 2px' }}>
              {t('fdeploy.tekanConnectSshMemulaiSesi')}
            </div>
          )}
        </div>
      ) : (
        <div ref={outRef} style={{
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          fontSize: 12.5, lineHeight: 1.55,
          background: '#0f172a', color: '#e2e8f0',
          borderRadius: '8px 8px 0 0',
          padding: '12px 14px',
          minHeight: 380, maxHeight: 520,
          overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {output || <span style={{ color: '#475569' }}>{t('fdeploy.tekanConnectSshMemulaiSesi')}</span>}
        </div>
      )}
      {/* Toolbar tombol khusus mobile (Esc/Tab/Ctrl/panah) — di atas input,
          jadi tampil persis di atas keyboard HP. Scroll horizontal bila sempit. */}
      {isMobile && connected && (
        <div style={{
          display: 'flex', gap: 6, overflowX: 'auto', background: '#1e293b',
          borderTop: '1px solid #334155', padding: '6px 8px', WebkitOverflowScrolling: 'touch',
        }}>
          {MOBILE_KEYS.map((k) => (
            <button key={k.label} onClick={() => sendKey(k.seq)}
              style={{
                flexShrink: 0, minWidth: 44, minHeight: 34, padding: '0 10px',
                background: '#334155', color: '#e2e8f0', border: '1px solid #475569',
                borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace',
              }}>
              {k.label}
            </button>
          ))}
        </div>
      )}
      {/* Input bar — MOBILE saja. Di desktop xterm sendiri yang menerima
          ketikan, jadi kotak kedua hanya akan membingungkan (dua tempat
          mengetik, satu yang benar). */}
      {isMobile && (
      <div style={{ display: 'flex', background: '#1e293b', borderRadius: '0 0 8px 8px', borderTop: '1px solid #334155', padding: '8px 12px', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ color: '#22c55e', fontFamily: 'monospace', fontSize: 13, userSelect: 'none', flexShrink: 0, lineHeight: '20px' }}>$</span>
        {/* textarea, BUKAN input. Menempel blok banyak baris ke <input> membuat
            jeda barisnya hilang diam-diam dan seluruh blok menyatu jadi satu
            perintah panjang — itu keluhan yang memicu perubahan ini.
            Tingginya mengikuti isi sampai 6 baris, lalu menggulir. */}
        <textarea
          ref={inputRef}
          value={inputVal}
          rows={Math.min(6, Math.max(1, inputVal.split('\n').length))}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!connected}
          placeholder={connected ? t('fdeploy.ketikCommandEnterKirim') : t('fdeploy.terhubung2')}
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#e2e8f0', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, caretColor: '#22c55e', resize: 'none', lineHeight: '20px', padding: 0 }}
        />
      </div>
      )}

      {/* Konfirmasi tempelan banyak baris. Ditampilkan APA ADANYA dan bernomor:
          yang perlu dibaca ulang sebelum menekan Enter adalah perintahnya
          sendiri, bukan ringkasan tentangnya. */}
      {pending && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.45)', padding: 16 }}
          onClick={() => setPending(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              Run {pending.length} commands on the server?
            </div>
            <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
              <pre style={{ margin: 0, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12, lineHeight: 1.7, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {pending.map((l, i) => `${String(i + 1).padStart(2, ' ')}  ${l}`).join('\n')}
              </pre>
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn size="sm" onClick={() => setPending(null)}>Cancel</Btn>
              <Btn size="sm" variant="primary" onClick={() => { const p = pending; setPending(null); execLines(p); }}>
                Run
              </Btn>
            </div>
          </div>
        </div>
      )}
      {/* Tips */}
      <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', lineHeight: 1.9 }}>
        {!isMobile ? (
          <>
            {/* Di desktop tombol-tombol ini ditangani xterm dan diteruskan ke
                PTY apa adanya, jadi riwayat perintah datang dari SHELL sendiri
                — bukan lagi riwayat lokal milik kotak input. */}
            <kbd style={kbdSt}>↑ ↓</kbd> shell history
            <kbd style={kbdSt}>{t('fdeploy.tab')}</kbd> {t('fdeploy.autocomplete')}
            <kbd style={kbdSt}>{t('fdeploy.ctrlC')}</kbd> {t('fdeploy.interupsi')}
            <kbd style={kbdSt}>{t('fdeploy.ctrlD')}</kbd> {t('fdeploy.eof')}
            <span style={{ marginLeft: 6 }}>Full-screen apps (top, htop, vi) supported.</span>
          </>
        ) : (
          <>
            <kbd style={kbdSt}>{t('fdeploy.enter')}</kbd> {t('fdeploy.kirim')}
            <kbd style={kbdSt}>Shift+Enter</kbd> new line
            <kbd style={kbdSt}>↑ ↓</kbd> history
            <kbd style={kbdSt}>{t('fdeploy.tab')}</kbd> {t('fdeploy.autocomplete')}
            <kbd style={kbdSt}>{t('fdeploy.ctrlC')}</kbd> {t('fdeploy.interupsi')}
            <kbd style={kbdSt}>{t('fdeploy.ctrlD')}</kbd> {t('fdeploy.eof')}
            <kbd style={kbdSt}>{t('fdeploy.ctrlL')}</kbd> {t('fdeploy.clearOutput')}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
// labelKey diterjemahkan saat render, bukan di sini.
// ── Riwayat Deploy (GLOBAL, lintas file & lintas root) ──────────────────────
// Melengkapi FileDiffHistoryModal yang bersifat per file. Panel ini memakai
// /api/file-deploy/diffs-recent lalu menampilkan hasilnya per SESI, sehingga
// pertanyaan "deploy tadi menyentuh file apa saja" terjawab dalam satu layar.
//
// Sesi = rekonstruksi dari timestamp diff (lihat SESSION_GAP_MS di backend),
// bukan ID yang tersimpan saat deploy. Untuk jejak resmi tetap pakai audit log
// (FILE_DEPLOY / FILE_DEPLOY_MULTI) — itu sebabnya panel ini menyebut dirinya
// alat tracing, bukan sumber kebenaran.
function DeployHistoryPanel({ api, notify }) {
  const { t } = useLang();

  const [type, setType]   = useState('');        // '' = semua root
  const [q, setQ]         = useState('');        // filter path (dikirim saat submit)
  const [data, setData]   = useState(null);      // { sessions, totalDiffs, scannedAll, sessionGapSeconds }
  const [busy, setBusy]   = useState(false);
  const [view, setView]   = useState(null);      // { name, rel, content } diff yang dibuka

  const load = useCallback(async (searchTerm) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ sessions: '20' });
      if (type) params.set('type', type);
      const term = (searchTerm !== undefined ? searchTerm : q).trim();
      if (term) params.set('q', term);
      const r = await api(`/api/file-deploy/diffs-recent?${params.toString()}`);
      setData(r);
    } catch (e) {
      notify('error', e?.message || t('fdeploy.histLoadFailed'));
      setData({ sessions: [], totalDiffs: 0, scannedAll: true });
    } finally { setBusy(false); }
  }, [api, notify, type, q, t]);

  // Muat awal + setiap ganti filter tipe. Pencarian teks sengaja pakai tombol
  // (bukan on-change) supaya tidak menembak request tiap ketikan.
  useEffect(() => { load(); }, [type]);  // eslint-disable-line

  const openDiff = async (item) => {
    setBusy(true);
    try {
      const r = await api(`/api/file-deploy/diff?type=${encodeURIComponent(item.type)}` +
        `&path=${encodeURIComponent(item.rel)}&name=${encodeURIComponent(item.name)}`);
      setView({ name: item.name, rel: item.rel, content: r.content });
    } catch (e) {
      notify('error', e?.message || t('fdeploy.histLoadFailed'));
    } finally { setBusy(false); }
  };

  const sessions = data?.sessions || [];

  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>{t('fdeploy.histSubtitle')}</p>

      {/* ── Filter ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <Select value={type} onChange={(e) => setType(e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('fdeploy.histAllTypes')}</option>
          <option value="frontend">frontend</option>
          <option value="backend">backend</option>
          <option value="ai-service">ai-service</option>
        </Select>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
          placeholder={t('fdeploy.histSearchPh')}
          style={{ width: 260 }}
        />
        <Btn onClick={() => load()} disabled={busy}>{t('fdeploy.histSearch')}</Btn>
        {q && <Btn size="sm" onClick={() => { setQ(''); load(''); }} disabled={busy}>{t('fdeploy.histClear')}</Btn>}
        <Btn variant="primary" size="sm" onClick={() => load()} disabled={busy}>{t('fdeploy.histRefresh')}</Btn>
      </div>

      {/* ── Ringkasan hasil ── */}
      {data && (
        <div style={S.locateBox('#f8fafc', '#e2e8f0')}>
          {t('fdeploy.histSummary', { s: sessions.length, d: data.totalDiffs, g: data.sessionGapSeconds || 120 })}
          {data.scannedAll === false && (
            <div style={{ marginTop: 4, color: '#b45309' }}>⚠️ {t('fdeploy.histCapped')}</div>
          )}
        </div>
      )}

      {busy && !data ? (
        <Empty icon="⏳" text={t('fdeploy.histLoading')} />
      ) : sessions.length === 0 ? (
        <Empty icon="📭" text={t('fdeploy.histEmpty')} />
      ) : (
        sessions.map((s) => (
          <div key={s.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
            {/* Kepala sesi */}
            <div style={{ background: '#f8fafc', padding: '9px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <b style={{ fontSize: 13, color: '#0f172a' }}>{fmtIsoWib(s.endedAt)}</b>
              {/* Rentang ditampilkan hanya bila sesinya memang membentang waktu */}
              {s.fileCount > 1 && s.startedAt !== s.endedAt && (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>← {fmtIsoWib(s.startedAt)}</span>
              )}
              <span style={{ fontSize: 12, color: '#475569' }}>{s.user}</span>
              <span style={{ fontSize: 12, color: '#475569' }}>{t('fdeploy.histFiles', { n: s.fileCount })}</span>
              <span style={{ fontSize: 12 }}>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>+{s.added}</span>{' '}
                <span style={{ color: '#dc2626', fontWeight: 600 }}>-{s.removed}</span>
              </span>
              {s.types.map((tp) => (
                <span key={tp} style={{ fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 999, padding: '1px 8px', color: '#475569' }}>{tp}</span>
              ))}
            </div>
            {/* Baris file dalam sesi */}
            {s.items.map((it) => (
              <div key={it.type + it.name} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <span style={{ fontSize: 11, color: '#64748b', minWidth: 74 }}>[{it.type}]</span>
                <code style={{ flex: 1, fontSize: 12, color: '#1e293b', wordBreak: 'break-all' }}>{it.rel}</code>
                <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>+{it.added}</span>{' '}
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>-{it.removed}</span>
                </span>
                {it.truncated && (
                  <span style={{ fontSize: 11, color: '#d97706' }} title={t('fdeploy.fileBerubahBesarDiringkasBukan')}>{t('fdeploy.ringkasan')}</span>
                )}
                <Btn size="sm" disabled={busy} onClick={() => openDiff(it)}>{t('fdeploy.lihat')}</Btn>
              </div>
            ))}
          </div>
        ))
      )}

      {/* ── Penampil diff (gaya sama dengan FileDiffHistoryModal) ── */}
      {view && (
        <Modal title={view.rel} onClose={() => setView(null)} maxWidth={860}>
          <pre style={{
            margin: 0, maxHeight: '60vh', overflow: 'auto', background: '#0f172a', color: '#e2e8f0',
            padding: 14, borderRadius: 8, fontSize: 12, lineHeight: 1.6,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}>
            {view.content.split('\n').map((ln, i) => {
              let color = '#e2e8f0';
              if (ln.startsWith('+') && !ln.startsWith('+++')) color = '#4ade80';
              else if (ln.startsWith('-') && !ln.startsWith('---')) color = '#f87171';
              else if (ln.startsWith('@@')) color = '#60a5fa';
              else if (ln.startsWith('#')) color = '#94a3b8';
              return <div key={i} style={{ color, whiteSpace: 'pre-wrap' }}>{ln || '\u00A0'}</div>;
            })}
          </pre>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <Btn onClick={() => setView(null)}>{t('fdeploy.tutup')}</Btn>
            <Btn variant="primary" onClick={() => downloadTextBlob(view.content, view.name)}>{t('fdeploy.download')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── AI Drafts → Deploy ────────────────────────────────────────────────────────
// Menutup celah manual terakhir: draft yang ditulis AI Coding ke
// AI_TOOLS_WRITE_ROOT selama ini harus diunduh lalu diunggah lagi hanya untuk
// berpindah folder di mesin yang SAMA.
//
// Yang TIDAK dilewati: gerbang sintaks, backup, riwayat diff, pengawas pasca
// restart, dan rollback. Semua tetap sama dengan unggahan manual — yang hilang
// hanya perjalanan berkas lewat browser.
//
// Teks disimpan LOKAL, bukan lewat t(). Gate build check-i18n.js menuntut
// paritas tiga bahasa untuk setiap key baru, dan panel ini tidak layak
// menggagalkan build hanya karena satu string belum diterjemahkan ke Arab.
function AiDraftsPanel({ api, notify }) {
  const { lang } = useLang();
  const L = ({
    id: {
      title: 'Draft AI Coding', reload: 'Muat ulang', none: 'Belum ada draft .js/.jsx.',
      check: 'Periksa', deploy: 'Deploy terpilih', checking: 'Memeriksa…', deploying: 'Men-deploy…',
      target: 'Tujuan', draft: 'Draft', size: 'Ukuran', status: 'Status',
      ambiguous: 'Banyak kandidat — isi manual', newFile: 'File baru', via: 'dicocokkan sebagai',
      okCheck: 'Lolos pemeriksaan', dupTarget: 'Tujuan bentrok dengan baris lain',
      ackDiff: 'Saya sudah memeriksa diff dan memahami peringatannya',
      warnTitle: 'Peringatan — periksa diff sebelum lanjut',
      noWarn: 'Tidak ada peringatan bentuk.', mustCheck: 'Jalankan Periksa dulu.',
      diff: 'Lihat diff', diffFail: 'Isi draft tidak bisa dibaca untuk diff.',
      diffTooBig: 'File terlalu besar untuk diff rinci. Bandingkan manual di server.',
      willRestart: 'Akan restart', willBuild: 'Akan rebuild frontend',
    },
    en: {
      title: 'AI Coding drafts', reload: 'Reload', none: 'No .js/.jsx drafts yet.',
      check: 'Check', deploy: 'Deploy selected', checking: 'Checking…', deploying: 'Deploying…',
      target: 'Target', draft: 'Draft', size: 'Size', status: 'Status',
      ambiguous: 'Multiple candidates — fill manually', newFile: 'New file', via: 'matched as',
      okCheck: 'Checks passed', dupTarget: 'Target collides with another row',
      ackDiff: 'I have reviewed the diff and understand the warnings',
      warnTitle: 'Warnings — review the diff before continuing',
      noWarn: 'No shape warnings.', mustCheck: 'Run Check first.',
      diff: 'View diff', diffFail: 'Draft content could not be read for diff.',
      diffTooBig: 'File too large for a detailed diff. Compare manually on the server.',
      willRestart: 'Will restart', willBuild: 'Will rebuild frontend',
    },
  })[lang === 'id' ? 'id' : 'en'];

  const [drafts, setDrafts] = useState([]);
  const [sel, setSel]       = useState({});    // name → { on, type, path }
  const [loading, setLoad]  = useState(false);
  const [checking, setChk]  = useState(false);
  const [busy, setBusy]     = useState(false);
  const [dry, setDry]       = useState(null);  // hasil dry run, key: path
  const [ack, setAck]       = useState(false);
  const [diffFor, setDiffFor] = useState(null);
  const [log, setLog]       = useState('');

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await api('/api/file-deploy/drafts');
      const list = r?.drafts || [];
      setDrafts(list);
      setSel((prev) => {
        const next = { ...prev };
        list.forEach((d) => {
          if (!next[d.name]) {
            next[d.name] = {
              on: false,
              type: d.suggest?.type || 'backend',
              path: d.suggest?.path || '',
            };
          }
        });
        return next;
      });
    } catch (e) { notify('error', e.message || 'Gagal memuat draft.'); }
    finally { setLoad(false); }
  }, [api, notify]);

  useEffect(() => { load(); }, [load]);

  // Setiap perubahan pilihan/tujuan membatalkan hasil pemeriksaan DAN
  // persetujuan. Tanpa ini, user bisa memeriksa satu berkas lalu menukar
  // tujuannya dan men-deploy dengan restu yang sudah tidak berlaku.
  const invalidate = () => { setDry(null); setAck(false); };
  const patch = (name, k, v) => { setSel((p) => ({ ...p, [name]: { ...p[name], [k]: v } })); invalidate(); };

  const chosen = drafts.filter((d) => sel[d.name]?.on);
  const items  = chosen.map((d) => ({ draft: d.name, type: sel[d.name].type, path: (sel[d.name].path || '').trim() }));
  const ready  = items.length > 0 && items.every((i) => i.path && i.type);

  // Dua baris yang menulis ke tujuan sama: yang belakangan menimpa yang duluan,
  // diam-diam. Lebih baik ditolak daripada menghasilkan hasil yang bergantung
  // pada urutan array.
  const dupKeys = items.map((i) => `${i.type}:${i.path}`);
  const dups    = new Set(dupKeys.filter((k, i) => dupKeys.indexOf(k) !== i));

  const anyWarn = dry ? Object.values(dry).some((f) => (f.warnings || []).length) : false;
  const dryOk   = dry ? dry.__ok : false;

  const runCheck = async () => {
    setChk(true); setAck(false);
    try {
      const r = await axiosApi.post('/api/file-deploy/deploy-drafts', { dryRun: true, items });
      // Dikunci per NAMA DRAFT, bukan `type:path`. Dua draft boleh menuju path
      // tujuan yang sama — itu justru lazim saat AI menulis ulang berkas dan
      // hasilnya otomatis diberi akhiran (-2). Dengan kunci lama keduanya
      // menempati slot yang sama, dan baris yang isinya bersih ikut menampilkan
      // peringatan milik baris lain. Deploy-nya sendiri tidak pernah salah
      // (hanya baris tercentang yang dikirim, dan `dups` menolak tujuan ganda),
      // tapi peringatan palsu pada baris yang benar melatih orang mengabaikan
      // peringatan — persis kebiasaan yang membuat fitur ini ada.
      const map = { __ok: r.data.ok };
      (r.data.files || []).forEach((f) => { if (f.draft) map[f.draft] = f; });
      (r.data.validation || []).filter((v) => !v.ok).forEach((v) => {
        if (!v.draft) return;
        map[v.draft] = { ...(map[v.draft] || {}), error: v.error };
      });
      setDry(map);
      notify(r.data.ok ? 'success' : 'error',
        r.data.ok ? L.okCheck : 'Pemeriksaan menemukan masalah — lihat detail per baris.');
    } catch (e) { notify('error', e.response?.data?.error || e.message); }
    finally { setChk(false); }
  };

  const runBuild = async () => {
    setLog('▶ Build…\n');
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/api/file-deploy/build`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader(); const dec = new TextDecoder(); let acc = '';
      for (;;) { const { done, value } = await reader.read(); if (done) break;
        const c = dec.decode(value, { stream: true }); acc += c; setLog((p) => p + c); }
      const m = /__BUILD_DONE__ exit=(\d+)/.exec(acc);
      return Boolean(m && m[1] === '0');
    } catch (e) { setLog((p) => p + `\n__BUILD_ERROR__ ${e.message}\n`); return false; }
  };

  const doDeploy = async () => {
    setBusy(true); setLog('');
    try {
      const r = await axiosApi.post('/api/file-deploy/deploy-drafts', { items });
      const d = r.data;
      const failed = (d.results || []).filter((x) => !x.ok);
      if (!d.okCount) { notify('error', `0/${d.total} ter-deploy. ${failed.map((f) => f.error).join('; ')}`); return; }
      notify(failed.length ? 'warning' : 'success', `${d.okCount}/${d.total} file ter-deploy.`);

      // Urutan sama dengan Multi Deploy: build frontend DULU, restart pm2
      // KEMUDIAN — dan backend paling akhir karena restartnya memutus koneksi.
      if (d.needBuild) {
        const ok = await runBuild();
        if (!ok && (d.pendingRestart || []).length) {
          notify('error', 'Build gagal — restart ditahan. Restart manual dari tab Operasi bila perlu.');
          return;
        }
      }
      for (const tp of (d.pendingRestart || [])) {
        setLog((p) => p + `\n▶ Restart ${tp}…\n`);
        try { await axiosApi.post('/api/file-deploy/restart', { type: tp }); }
        catch (_) { setLog((p) => p + '  (koneksi terputus — normal bila ini restart terakhir)\n'); }
        await new Promise((res) => setTimeout(res, 2500));
      }
      await load(); setDry(null); setAck(false);
    } catch (e) { notify('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  // Deploy terkunci sampai: ada pilihan, tujuan terisi, tidak ada tujuan
  // bentrok, pemeriksaan sudah dijalankan dan lolos, dan — bila ada peringatan
  // bentuk — user menyatakan sudah memeriksa diff. Peringatan tidak memblokir
  // sendiri; yang memblokir adalah belum diakuinya peringatan itu.
  const canDeploy = ready && dups.size === 0 && dryOk && (!anyWarn || ack) && !busy && !checking;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <b style={{ fontSize: 14 }}>{L.title}</b>
        <Btn size="sm" onClick={load} disabled={loading || busy}>{L.reload}</Btn>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Btn size="sm" onClick={runCheck} disabled={!ready || dups.size > 0 || checking || busy}>
            {checking ? L.checking : L.check}
          </Btn>
          <Btn size="sm" variant="primary" onClick={doDeploy} disabled={!canDeploy}>
            {busy ? L.deploying : L.deploy}
          </Btn>
        </span>
      </div>

      {drafts.length === 0 ? <Empty icon="🧪" text={L.none} /> : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          {drafts.map((d) => {
            const s = sel[d.name] || { on: false, type: 'backend', path: '' };
            const key = `${s.type}:${(s.path || '').trim()}`;   // hanya untuk deteksi tujuan ganda
            const info = dry ? dry[d.name] : null;                // hasil check: per draft
            const warns = info?.warnings || [];
            const isDup = s.on && dups.has(key);
            return (
              <div key={d.name} style={{ borderBottom: '1px solid #eef2f7', padding: '10px 12px', background: s.on ? '#f8fafc' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" checked={s.on} onChange={(e) => patch(d.name, 'on', e.target.checked)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {fmtBytes(d.bytes)}
                      {d.resolvedAs && d.resolvedAs !== d.name ? ` · ${L.via} ${d.resolvedAs}` : ''}
                      {d.ambiguous ? ` · ${L.ambiguous}` : ''}
                    </div>
                  </div>
                  <Select value={s.type} onChange={(e) => patch(d.name, 'type', e.target.value)} style={{ width: 130 }}>
                    <option value="backend">backend</option>
                    <option value="frontend">frontend</option>
                    <option value="ai-service">ai-service</option>
                  </Select>
                  <Input value={s.path} placeholder="path/tujuan.js" style={{ width: 300 }}
                    onChange={(e) => patch(d.name, 'path', e.target.value)} />
                </div>

                {isDup && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>⛔ {L.dupTarget}</div>
                )}
                {info?.error && (
                  <pre style={{ marginTop: 6, fontSize: 11, color: '#b91c1c', whiteSpace: 'pre-wrap' }}>{info.error}</pre>
                )}
                {info && !info.error && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <span style={{ color: '#64748b' }}>
                      {info.exists ? `${fmtBytes(info.bytesOld)} → ${fmtBytes(info.bytesNew)}` : L.newFile}
                    </span>
                    {info.exists && (
                      <Btn size="sm" onClick={() => setDiffFor({ name: d.name, type: s.type, path: s.path })}>{L.diff}</Btn>
                    )}
                    {warns.length === 0
                      ? <span style={{ marginLeft: 8, color: '#15803d' }}>✓ {L.noWarn}</span>
                      : (
                        <div style={{ marginTop: 6, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 12, marginBottom: 4 }}>⚠ {L.warnTitle}</div>
                          {warns.map((w, i) => <div key={i} style={{ fontSize: 12, color: '#7f1d1d' }}>• {w}</div>)}
                        </div>
                      )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {anyWarn && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          {L.ackDiff}
        </label>
      )}
      {!dry && ready && <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>{L.mustCheck}</div>}

      {log && (
        <pre style={{ marginTop: 12, background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8,
                      fontSize: 11, maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{log}</pre>
      )}

      {diffFor && (
        <DraftDiffModal api={api} draft={diffFor} onClose={() => setDiffFor(null)} label={L} />
      )}
    </div>
  );
}

// Diff draft vs file tujuan. Isi draft diambil sebagai teks dari endpoint unduh
// aiDrafts; bila gagal (mis. izin ai_coding tidak dimiliki), diff dimatikan
// dengan pesan jelas — BUKAN ditampilkan kosong seolah tidak ada perbedaan.
function DraftDiffModal({ api, draft, onClose, label }) {
  const [state, setState] = useState({ loading: true, error: '', rows: [] });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const cur = await api('/api/file-deploy/preview', {
          method: 'POST', body: JSON.stringify({ type: draft.type, targetPath: draft.path }),
        });
        const token = localStorage.getItem('auth_token');
        const resp = await fetch(`${API_BASE}/api/ai/drafts/file/${encodeURIComponent(draft.name)}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error(label.diffFail);
        const text = await resp.text();
        if (dead) return;
        // diffLines mengembalikan null bila matriks LCS-nya terlalu besar
        // (>4 juta sel). Tanpa penjagaan ini, .map() di bawah melempar dan
        // modalnya blank — terlihat seperti diff kosong alias "tidak ada beda",
        // yang justru kebalikan dari keadaan sebenarnya.
        const rows = diffLines(cur?.content || '', text);
        if (!rows) { setState({ loading: false, error: label.diffTooBig, rows: [] }); return; }
        setState({ loading: false, error: '', rows });
      } catch (e) { if (!dead) setState({ loading: false, error: e.message || label.diffFail, rows: [] }); }
    })();
    return () => { dead = true; };
  }, [api, draft, label]);

  return (
    <Modal title={`${draft.name} → ${draft.type}:${draft.path}`} onClose={onClose} maxWidth={980}>
      {state.loading ? <div style={{ padding: 16, color: '#64748b' }}>…</div>
        : state.error ? <div style={{ padding: 16, color: '#b91c1c' }}>{state.error}</div>
        : (
          <pre style={{ margin: 0, maxHeight: '60vh', overflow: 'auto', fontSize: 11.5, lineHeight: 1.5 }}>
            {state.rows.map((r, i) => (
              <div key={i} style={{
                background: r.type === 'add' ? '#ecfdf5' : r.type === 'del' ? '#fef2f2' : 'transparent',
                color:      r.type === 'add' ? '#065f46' : r.type === 'del' ? '#991b1b' : '#334155',
                whiteSpace: 'pre-wrap',
              }}>{r.type === 'add' ? '+ ' : r.type === 'del' ? '- ' : '  '}{r.text}</div>
            ))}
          </pre>
        )}
    </Modal>
  );
}

// ── Deploy dari arsip .zip ───────────────────────────────────────────────────
// Alur dua langkah yang sengaja dipisah:
//
//   1. Unggah zip → server membongkar, memetakan tujuan dari DEPLOY-MANIFEST.json,
//      menjalankan gerbang validasi untuk SELURUH batch, dan menahan isinya.
//      Belum ada satu byte pun yang ditulis ke produksi.
//   2. Setelah rencananya ditinjau → tulis semua, lalu build & restart otomatis.
//
// "Otomatis" di sini berarti tujuan tidak perlu diisi satu per satu dan
// rangkaian build→restart berjalan sendiri. BUKAN berarti perubahan masuk tanpa
// dilihat: tombol deploy tetap terkunci sampai ringkasan diff dibuka.
function ZipDeployPanel({ api, notify }) {
  const [file, setFile]       = useState(null);
  const [staging, setStaging] = useState(false);
  const [plan, setPlan]       = useState(null);
  const [busy, setBusy]       = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [log, setLog]         = useState('');
  const [building, setBuilding] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setPlan(null); setReviewed(false); setLog(''); setFile(null); if (inputRef.current) inputRef.current.value = ''; };

  const doStage = async (f) => {
    if (!f) return;
    setStaging(true); setPlan(null); setReviewed(false); setLog('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axiosApi.post('/api/file-deploy/zip-stage', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPlan(res.data);
      if (!res.data.hasManifest) {
        notify('warning', 'Arsip tidak memuat DEPLOY-MANIFEST.json — tujuan ditebak dari nama berkas dan sebagian tidak bisa ditentukan.');
      }
    } catch (e) {
      notify('error', e.response?.data?.error || e.message);
      reset();
    } finally { setStaging(false); }
  };

  const cancelBatch = async () => {
    if (plan?.batchId) { try { await api(`/api/file-deploy/zip-stage/${plan.batchId}`, { method: 'DELETE' }); } catch (_) {} }
    reset();
  };

  // Salinan pola runBuild dari MultiDeployPanel: log build di-stream, dan
  // nilai kembaliannya menentukan apakah restart boleh dilanjutkan.
  const runBuild = async () => {
    setBuilding(true); setLog((p) => p + '\n▶ Memulai build frontend…\n');
    let ok = false;
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE}/api/file-deploy/build`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        acc += chunk;
        setLog((p) => p + chunk);
      }
      ok = /__BUILD_DONE__\s*0\b/.test(acc) || /Compiled successfully/i.test(acc);
    } catch (e) {
      setLog((p) => p + `\n__BUILD_ERROR__ ${e.message}\n`);
    } finally { setBuilding(false); }
    return ok;
  };

  const doDeploy = async () => {
    setBusy(true); setLog('');
    try {
      const res = await axiosApi.post('/api/file-deploy/zip-deploy', { batchId: plan.batchId, acknowledged: true });
      const d = res.data;
      const failed = (d.results || []).filter((x) => !x.ok);
      setLog(`✓ ${d.okCount}/${d.total} berkas tertulis.\n` +
             (failed.length ? `✗ Gagal: ${failed.map((f) => f.path).join(', ')}\n` : ''));
      if (!d.okCount) { notify('error', 'Tidak ada berkas yang tertulis.'); return; }

      // Build DULU, restart pm2 KEMUDIAN — membalik urutannya berarti restart
      // berjalan saat build masih memakai CPU/memori.
      if (d.needBuild) {
        const buildOk = await runBuild();
        if (!buildOk && (d.pendingRestart || []).length) {
          notify('error', 'Build gagal — restart DITAHAN. Berkas sudah tertulis; restart manual dari tab Operasi setelah masalah build diperbaiki.');
          return;
        }
      }

      for (const tp of (d.pendingRestart || [])) {
        setLog((p) => p + `\n▶ Restart ${tp}…\n`);
        try { await axiosApi.post('/api/file-deploy/restart', { type: tp }); }
        catch (_) { setLog((p) => p + '  (koneksi terputus — normal bila ini restart terakhir)\n'); }
        if ((d.pendingRestart || []).length > 1) await new Promise((r) => setTimeout(r, 2500));
      }
      setLog((p) => p + '\n✓ Selesai.\n');
      notify('success', `${d.okCount}/${d.total} berkas ter-deploy.`);
      setPlan(null); setReviewed(false); setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      const err = e.response?.data;
      if (err?.blocked) {
        setLog('✗ VALIDASI GAGAL — tidak ada yang ditulis.\n\n' + (err.error || ''));
        notify('error', 'Batch ditolak gerbang validasi. Tidak ada berkas yang berubah.');
      } else {
        notify('error', err?.error || e.message);
      }
    } finally { setBusy(false); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', fontSize: 13, color: '#0f172a', borderBottom: '1px solid #f1f5f9' };

  const rows = plan?.plan || [];
  const okRows = rows.filter((r) => !r.error);
  const badRows = rows.filter((r) => r.error);

  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
        Unggah arsip <b>.zip</b> hasil "Download All". Bila arsip memuat <code>DEPLOY-MANIFEST.json</code>,
        tujuan tiap berkas terbaca langsung dari sana — termasuk berkas baru dan berkas berawalan layanan
        (<code>ai-service.server.js</code> → <code>ai-service/server.js</code>) yang tidak bisa ditebak dari namanya.
        Validasi, backup, diff, dan watchdog tetap berlaku seperti unggah biasa.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          ref={inputRef} type="file" accept=".zip,application/zip"
          disabled={staging || busy}
          onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); doStage(f); }}
          style={{ fontSize: 13 }}
        />
        {staging && <span style={{ fontSize: 12, color: '#64748b' }}>Membongkar & memvalidasi…</span>}
        {plan && <Btn size="sm" onClick={cancelBatch} disabled={busy}>Batalkan batch</Btn>}
      </div>

      {plan && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <Badge text={plan.hasManifest ? '📋 manifest terbaca' : '⚠️ tanpa manifest'} color={plan.hasManifest ? '#059669' : '#d97706'} />
            <Badge text={plan.validationOk ? '✓ validasi lolos' : '✗ validasi gagal'} color={plan.validationOk ? '#059669' : '#dc2626'} />
            <Badge text={`${okRows.length} berkas siap`} color="rgb(var(--brand-600))" />
            {badRows.length > 0 && <Badge text={`${badRows.length} bermasalah`} color="#dc2626" />}
            {plan.manifestBatch && <Badge text={plan.manifestBatch} color="#64748b" />}
          </div>

          {plan.missing?.length > 0 && (
            <Alert type="error" msg={`Manifest menyebut berkas yang tidak ada di arsip: ${plan.missing.join(', ')}. Unduhan kemungkinan tidak lengkap — deploy diblokir.`} />
          )}
          {plan.extra?.length > 0 && (
            <Alert type="info" msg={`Arsip memuat berkas yang tidak disebut manifest (diabaikan): ${plan.extra.join(', ')}`} />
          )}
          {!plan.validationOk && (
            <Alert type="error" msg={plan.validationSummary || 'Gerbang validasi menolak batch ini.'} />
          )}

          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Berkas</th><th style={th}>Tujuan</th><th style={th}>Status</th><th style={th}>Perubahan</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.name}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
                      {r.error ? <span style={{ color: '#dc2626' }}>—</span> : `${r.type}/${r.target}`}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {r.error   ? <Badge text={r.error} color="#dc2626" />
                       : r.isNew ? <Badge text="baru" color="rgb(var(--brand-600))" />
                       : r.identical ? <Badge text="IDENTIK" color="#d97706" />
                       : <Badge text="menimpa" color="#64748b" />}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: '#475569' }}>
                      {r.error ? '—'
                        : r.isNew ? `berkas baru · ${r.sizeAfter ?? r.size} B`
                        : `${r.linesBefore ?? '?'} → ${r.linesAfter ?? '?'} baris · ${r.sizeBefore ?? '?'} → ${r.sizeAfter ?? r.size} B`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.some((r) => r.identical) && (
            <Alert type="info" msg="Sebagian berkas isinya IDENTIK dengan yang sudah ada di server — biasanya tanda berkas lama yang ikut terunggah. Periksa sebelum lanjut." />
          )}

          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#334155', marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              Saya sudah meninjau tujuan dan ringkasan perubahan di atas.
              {plan.plan?.some((r) => r.type === 'frontend') &&
                ' Frontend akan di-build ulang sebelum restart — prosesnya beberapa menit.'}
            </span>
          </label>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn
              variant="primary"
              disabled={!plan.ready || !reviewed || busy || building}
              onClick={doDeploy}
            >
              {busy || building ? '⏳ Menjalankan…' : '🚀 Deploy semua + build & restart otomatis'}
            </Btn>
            {!plan.ready && (
              <span style={{ fontSize: 12, color: '#dc2626' }}>
                Batch belum siap — perbaiki masalah di atas lebih dulu.
              </span>
            )}
          </div>
        </>
      )}

      {log && (
        <pre style={{ marginTop: 14, padding: 12, background: '#0f172a', color: '#e2e8f0', borderRadius: 8, fontSize: 12, maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {log}
        </pre>
      )}

      {!plan && !staging && <Empty icon="📦" text="Pilih arsip .zip untuk memulai." />}
    </div>
  );
}

// ── Git — riwayat commit otomatis dari jalur deploy ─────────────────────────
// Sumber: GET /api/file-deploy/git (gitDeploy.js). Read-only murni — panel ini
// TIDAK memanggil commit/checkout/revert apa pun. Menjawab satu pertanyaan:
// "apakah working tree bersih, dan apa saja yang sudah tercatat sebagai
// deploy?" Kalau clean=false, ada perubahan di server yang tidak lewat jalur
// deploy — suntingan manual lewat SSH, atau commit yang gagal diam-diam.
function GitPanel({ api, notify }) {
  const [data, setData] = useState(null);   // { root, status, recent }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api('/api/file-deploy/git?limit=30');
      setData(r);
    } catch (e) {
      notify('error', e?.message || 'Gagal memuat riwayat git.');
      setData(null);
    } finally { setBusy(false); }
  }, [api, notify]);

  useEffect(() => { load(); }, [load]);

  const status = data?.status;
  const recent = data?.recent;

  // Dua sumber ini bisa gagal independen (mis. repo ada tapi rev-parse gagal),
  // jadi status "tidak siap" dicek terpisah untuk masing-masing alih-alih satu
  // flag tunggal yang menyembunyikan yang mana sebenarnya bermasalah.
  const statusUnready = status && !status.ok;
  const recentUnready = recent && !recent.ok;

  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>
        Riwayat commit otomatis dari deploy. Hanya membaca — tidak ada aksi
        commit/revert di sini.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <Btn variant="primary" size="sm" onClick={load} disabled={busy}>
          {busy ? 'Memuat…' : '↻ Segarkan'}
        </Btn>
        {data?.root && (
          <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center' }}>
            root: <code>{data.root}</code>
          </span>
        )}
      </div>

      {/* ── Status working tree ── */}
      {statusUnready ? (
        <div style={S.locateBox('#fffbeb', '#fde68a')}>
          ⚠️ Pencatatan git tidak aktif: {status.reason || status.error || 'sebab tidak diketahui'}
        </div>
      ) : status ? (
        <div style={S.locateBox(
          status.clean ? '#f0fdf4' : '#fffbeb',
          status.clean ? '#bbf7d0' : '#fde68a',
        )}>
          {status.clean
            ? '✅ Working tree bersih — semua perubahan server tercatat lewat deploy.'
            : `⚠️ Ada ${status.count} perubahan yang BELUM tercatat sebagai commit deploy.`}
          {!status.clean && status.entries?.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
              {status.entries.map((e, i) => (
                <li key={i}><code>{e.status}</code> {e.file}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* ── Riwayat commit ── */}
      <div style={{ marginTop: 16 }}>
        {busy && !data ? (
          <Empty icon="⏳" text="Memuat riwayat…" />
        ) : recentUnready ? (
          <Empty icon="⚠️" text={recent.reason || recent.error || 'Riwayat tidak tersedia.'} />
        ) : !recent?.items?.length ? (
          <Empty icon="📭" text="Belum ada commit tercatat." />
        ) : (
          recent.items.map((c) => (
            <div key={c.hash} style={{
              display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 12px',
              borderBottom: '1px solid #f1f5f9', fontSize: 13,
            }}>
              <code style={{ color: 'rgb(var(--brand-600))', minWidth: 64 }}>{c.hash}</code>
              <span style={{ flex: 1, color: '#1e293b' }}>{c.subject}</span>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{c.author}</span>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {fmtIsoWib(new Date(c.at).toISOString())}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Tab: Console Log ────────────────────────────────────────────────
// Jejak audit OS Console dari tabel os_console_log: sesi dibuka, tiap baris
// perintah, dan sesi ditutup. KELUARAN perintah tidak disimpan — lihat catatan
// di backend/sshConsoleWs.js.
function ConsoleLogPanel() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [days, setDays] = useState('30');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const LIMIT = 100;

  const load = useCallback(async (off) => {
    setBusy(true); setErr('');
    try {
      const p = new URLSearchParams({ limit: String(LIMIT), offset: String(off || 0) });
      if (q) p.set('q', q);
      if (kind) p.set('kind', kind);
      if (days) p.set('days', days);
      const d = await api('/api/console-log?' + p.toString());
      setRows(d.rows || []); setTotal(d.total || 0); setOffset(off || 0);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [api, q, kind, days]);

  useEffect(() => { load(0); }, [load]);

  const KIND_STYLE = {
    connect:    { bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0' },
    command:    { bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe' },
    disconnect: { bg: '#f1f5f9', fg: '#475569', bd: '#cbd5e1' },
  };
  const cell = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5, verticalAlign: 'top' };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') load(0); }}
          placeholder="Search command, user, or IP…"
          style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, minWidth: 260 }} />
        <select value={kind} onChange={e => setKind(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}>
          <option value="">All events</option>
          <option value="command">Commands</option>
          <option value="connect">Session start</option>
          <option value="disconnect">Session end</option>
        </select>
        <select value={days} onChange={e => setDays(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}>
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="365">Last year</option>
          <option value="">All time</option>
        </select>
        <button onClick={() => load(0)} disabled={busy}
          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
          {busy ? 'Loading…' : 'Apply'}
        </button>
        <span style={{ fontSize: 12.5, color: '#64748b' }}>{total.toLocaleString('en-US')} entries</span>
      </div>

      {err && (
        <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 12.5 }}>{err}</div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#475569' }}>
              <th style={cell}>Time</th>
              <th style={cell}>User</th>
              <th style={cell}>Event</th>
              <th style={cell}>Command</th>
              <th style={cell}>IP</th>
              <th style={cell}>Session</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const st = KIND_STYLE[r.kind] || KIND_STYLE.disconnect;
              return (
                <tr key={r.id}>
                  <td style={{ ...cell, whiteSpace: 'nowrap', color: '#64748b' }}>
                    {String(r.created_at || '').slice(0, 19).replace('T', ' ')}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.username || '—'}</td>
                  <td style={cell}>
                    <span style={{ padding: '1px 8px', borderRadius: 999, fontSize: 11.5,
                      background: st.bg, color: st.fg, border: '1px solid ' + st.bd }}>{r.kind}</span>
                  </td>
                  <td style={{ ...cell, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                    {r.kind === 'command'
                      ? r.command
                      : (r.bytes_out != null
                          ? <span style={{ color: '#94a3b8' }}>{r.bytes_out.toLocaleString('en-US')} bytes of output</span>
                          : <span style={{ color: '#94a3b8' }}>—</span>)}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap', color: '#64748b' }}>{r.ip || '—'}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap', color: '#94a3b8', fontSize: 11.5 }}>{r.session_id}</td>
                </tr>
              );
            })}
            {!rows.length && !busy && (
              <tr><td style={{ ...cell, color: '#64748b' }} colSpan={6}>No console activity in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 12.5 }}>
        <button onClick={() => load(Math.max(0, offset - LIMIT))} disabled={offset <= 0 || busy}
          style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Previous</button>
        <span style={{ color: '#64748b' }}>{total ? offset + 1 : 0}–{Math.min(offset + LIMIT, total)} of {total.toLocaleString('en-US')}</span>
        <button onClick={() => load(offset + LIMIT)} disabled={offset + LIMIT >= total || busy}
          style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Next</button>
      </div>

      <p style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
        Command lines are recorded; command OUTPUT is not stored — only its size, so passwords and
        file contents printed to the terminal never land in the database.
      </p>
    </div>
  );
}

const REMOTE_HOSTS = [
  { key: 'rconsole', target: 'dw',     label: 'ubuntu@10.40.1.58',     tab: '10.40.1.58 (DW)' },
  { key: 'rc18150',  target: 'h18150', label: 'ec2-user@10.32.18.150', tab: '10.32.18.150' },
  { key: 'rc18170',  target: 'h18170', label: 'ec2-user@10.32.18.170', tab: '10.32.18.170' },
  { key: 'rc1820',   target: 'h1820',  label: 'ec2-user@10.32.18.20',  tab: '10.32.18.20' },
  { key: 'rc1830',   target: 'h1830',  label: 'ec2-user@10.32.18.30',  tab: '10.32.18.30' },
  { key: 'rc1910',   target: 'h1910',  label: 'ec2-user@10.32.19.10',  tab: '10.32.19.10' },
  { key: 'rc1811',   target: 'h1811',  label: 'ec2-user@10.32.18.11',  tab: '10.32.18.11' },
  { key: 'rc1812',   target: 'h1812',  label: 'ec2-user@10.32.18.12',  tab: '10.32.18.12' },
  { key: 'rc1813',   target: 'h1813',  label: 'ec2-user@10.32.18.13',  tab: '10.32.18.13' },
  { key: 'rc19198', target: 'h19198', label: 'ec2-user@10.32.19.198', tab: '10.32.19.198 (PIL_NAT)' },
  { key: 'rc32018', target: 'h32018', label: '10.32.0.18 via 10.40.1.58', tab: '10.32.0.18 (On-Prem)' },
  { key: 'rc320231', target: 'h320231', label: '10.32.0.231 via 10.40.1.58', tab: '10.32.0.231 (On-Prem)' },
];
// Satu tab 'Remote Console' berisi sub-tab per host. Panel host dipasang saat pertama
// dibuka lalu hanya disembunyikan (display:none) - sesi SSH tetap hidup saat pindah host.
function RemoteConsoleHub() {
  const [active, setActive] = useState(REMOTE_HOSTS[0].key);
  const [mounted, setMounted] = useState(() => new Set([REMOTE_HOSTS[0].key]));
  const pick = (k) => {
    setActive(k);
    setMounted(m => (m.has(k) ? m : new Set([...m, k])));
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {REMOTE_HOSTS.map(h => {
          const on = active === h.key;
          return (
            <button key={h.key} type="button" onClick={() => pick(h.key)} title={h.label}
              style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                fontWeight: on ? 600 : 500,
                border: '1px solid ' + (on ? 'rgb(var(--brand-600))' : '#e2e8f0'),
                background: on ? 'rgb(var(--brand-50))' : '#ffffff',
                color: on ? 'rgb(var(--brand-700))' : '#475569' }}>
              {mounted.has(h.key) ? '● ' : ''}{h.tab}
            </button>
          );
        })}
      </div>
      {REMOTE_HOSTS.filter(h => mounted.has(h.key)).map(h => (
        <div key={h.key} style={{ display: active === h.key ? 'block' : 'none' }}>
          <OsConsolePanel target={h.target} targetLabel={h.label} />
        </div>
      ))}
    </div>
  );
}

const TABS_ALL = [
  { key: 'deploy',   labelKey: 'fdeploy.tabDeploy',   icon: '🚀', component: DeployPanel },
  { key: 'multi',    labelKey: 'fdeploy.tabMulti',    icon: '📑', component: MultiDeployPanel },
  { key: 'zip',      labelKey: null,                  icon: '📦', component: ZipDeployPanel, rawLabel: 'Zip Deploy' },
  { key: 'drafts',   labelKey: null,                  icon: '🧪', component: AiDraftsPanel, rawLabel: 'AI Drafts' },
  { key: 'history',  labelKey: 'fdeploy.tabHistory',  icon: '🕘', component: DeployHistoryPanel },
  { key: 'git',      labelKey: null,                  icon: '🌿', component: GitPanel, rawLabel: 'Git' },
  { key: 'backup',   labelKey: 'fdeploy.tabBackup',   icon: '🗂️', component: BackupPanel },
  { key: 'ops',      labelKey: 'fdeploy.tabOps',      icon: '🛠️', component: OpsPanel },
  { key: 'explorer', labelKey: 'fdeploy.tabExplorer', icon: '🗄️', component: FileExplorerPanel },
  { key: 'console',  labelKey: 'fdeploy.tabConsole',  icon: '💻', component: OsConsolePanel },
  { key: 'remote', labelKey: null, icon: '🌐', component: RemoteConsoleHub, rawLabel: 'Remote Console' },
  { key: 'conlog', labelKey: null, icon: '📜', component: ConsoleLogPanel, rawLabel: 'Console Log' },
];
// Source: searchRegistry.js (pageKey 'file_deploy'); unregistered tabs are not rendered.
const FD_TABS = tabsFor('file_deploy');
const TABS = TABS_ALL.filter(t => FD_TABS.includes(t.key));

export default function FileDeployPage() {
  const {
    t
  } = useLang();

  const { user } = useAuth();
  const api = useApi();
  useLang(); // disiapkan untuk i18n Stage 5; teks saat ini Bahasa Indonesia
  const [searchParams] = useSearchParams();
  // Deep link ?tab= — dipakai QuickSearch (Ctrl+K) untuk melompat langsung ke
  // tab tertentu. Divalidasi terhadap TABS, bukan dipakai mentah: nilai asing
  // di URL akan membuat ActivePanel undefined dan halaman blank.
  //
  // Lazy initializer supaya tab yang benar terpasang pada render PERTAMA —
  // dengan efek saja, tab default sempat berkedip lebih dulu. Sisi reaktifnya
  // (halaman ini tidak pernah di-unmount) ditangani useTabDeepLink.
  const [activeKey, setActiveKey] = useState(() => {
    const p = searchParams.get('tab');
    return TABS.some(tb => tb.key === p) ? p : 'deploy';
  });
  useTabDeepLink('file_deploy', (p) => TABS.some(tb => tb.key === p), setActiveKey);
  const [alert, setAlert] = useState(null);

  const notify = useCallback((type, msg) => setAlert({ type, msg }), []);

  // Guard di UI (sumber kebenaran tetap backend: __admin__).
  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Empty icon="🔒" text={t('fdeploy.halamanHanyaAdmin')} />
      </div>
    );
  }

  const ActivePanel = TABS.find(t => t.key === activeKey)?.component;

  // ── Tab yang keadaannya HARUS bertahan saat berpindah tab ─────────────────
  // Render lama (`{ActivePanel && <ActivePanel/>}`) MELEPAS panel setiap kali
  // tab berganti. Untuk sebagian besar tab itu tidak apa-apa — mereka memuat
  // ulang dari server dan hasilnya sama. Untuk dua tab ini akibatnya nyata:
  //
  //   • console  — cleanup di OsConsolePanel memanggil `wsRef.current.close()`,
  //                jadi SESI SSH-nya BENAR-BENAR MATI di server, bukan sekadar
  //                hilang dari layar. Kembali ke tab ini berarti login ulang
  //                dan kehilangan seluruh keluaran serta direktori kerja.
  //   • explorer — path yang sedang dibuka hilang, selalu kembali ke root.
  //
  // Keduanya dipasang saat PERTAMA KALI dibuka lalu dibiarkan hidup,
  // disembunyikan dengan `display:none`. Sengaja TIDAK semua tab: memasang
  // kesepuluh panel akan memicu pemanggilan API dari tab yang belum pernah
  // dibuka, dan sebagian di antaranya melakukan polling.
  //
  // KONSEKUENSI YANG DITERIMA (disetujui pemilik sistem): sesi SSH tetap hidup
  // di latar selama halaman File Deploy terbuka. Ia baru tertutup saat halaman
  // ditinggalkan atau tombol Disconnect ditekan — bukan lagi saat pindah tab.
  const KEEP_ALIVE = ['console', 'explorer', 'remote'];
  // Dicatat di ref, bukan state: menambah anggota tidak perlu memicu render.
  // Render yang dibutuhkan sudah dipicu oleh setActiveKey.
  const mountedRef = useRef(new Set());
  if (KEEP_ALIVE.includes(activeKey)) mountedRef.current.add(activeKey);

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>{t('fdeploy.fileDeploy')}</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 0 }}>
          {t('fdeploy.unggahFileJsJsxLangsung')}
        </p>
      </div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveKey(tab.key)}
            style={{ padding: '9px 16px', fontSize: 13, cursor: 'pointer', border: 'none', borderBottom: activeKey === tab.key ? '2px solid rgb(var(--brand-600))' : '2px solid transparent', background: 'none', color: activeKey === tab.key ? 'rgb(var(--brand-600))' : '#64748b', fontWeight: activeKey === tab.key ? 700 : 500, fontFamily: 'inherit', marginBottom: -1, whiteSpace: 'nowrap' }}>
            {/* rawLabel: label harfiah untuk tab yang belum punya key i18n.
                Menambah key baru berarti wajib melengkapi tiga bahasa, dan
                gate check-i18n.js akan menggagalkan build bila salah satu
                tertinggal — tidak sepadan untuk satu nama tab. */}
            {tab.icon} {tab.rawLabel || t(tab.labelKey)}
          </button>
        ))}
      </div>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {/* Panel keep-alive: sekali dipasang, tidak pernah dilepas — hanya
            disembunyikan. `key` wajib stabil supaya React tidak menyusun
            ulang pohonnya (yang akan melepasnya dan membunuh sesi SSH). */}
        {TABS.filter(tb => KEEP_ALIVE.includes(tb.key) && mountedRef.current.has(tb.key)).map(tb => (
          <div key={tb.key} style={{ display: activeKey === tb.key ? 'block' : 'none' }}>
            <tb.component api={api} notify={notify} />
          </div>
        ))}
        {/* Tab lain: perilaku lama, dipasang dan dilepas seperti sebelumnya. */}
        {!KEEP_ALIVE.includes(activeKey) && ActivePanel && <ActivePanel api={api} notify={notify} />}
      </div>
    </div>
  );
}
